'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import * as Y from 'yjs';
import {
  isOnCanvas,
  type AnnotationColor,
  type AnnotationKind,
  type AnnotationShape,
  type WorkspaceDTO,
  type WorkspaceItemDTO,
  type WorkspaceItemState,
  type WorkspaceItemType,
  type WorkspaceSettings,
} from '@/lib/workspaces/types';
import {
  findEmptySpace,
  findScreenGridSlot,
} from '@/lib/workspaces/find-empty-space';
import {
  SCREEN_CELL,
  SETTLE_MIN_COVERAGE,
  entryOfItem,
  isScreenType,
  newScreenName,
  orderScreenEntries,
  pickSettleScreen,
  rectsIntersect,
  type ScreenRect,
  type ViewportTransform,
} from '@/lib/workspaces/screens';
import { placeOpenWindow } from '@/lib/workspaces/open-placement';
import {
  decideLeave,
  displayScreenEntries,
  screenViewRect,
} from '@/lib/workspaces/stage-rules';
import { recallFullscreen, rememberFullscreen, saveCurrentScreen } from '@/lib/workspaces/screen-state';
import { topAdWindowId } from '@/lib/workspaces/top-ad-window';
import { presentedWindowId } from '@/lib/workspaces/handset-present';
import { bestFitSize, EXPAND_MARGIN, type Size } from '@/lib/workspaces/best-fit';
import { emitWorkspaceItemsChanged } from '@/lib/workspaces/list-events';
import { isSensitiveFeature } from '@/lib/workspaces/widget-permissions';
import {
  readLocalWidgetGrants,
  subscribeLocalWidgetGrants,
} from '@/lib/workspaces/widget-permissions-local';
import { useCanvasYjs } from './canvas/yjs-mount';

/**
 * Canvas item ↔ Y.Map projection (unified CRDT model).
 *
 * The workspace canvas lives in a workspace-level Y.Doc: `Y.Map('items')` keyed
 * by itemId → a per-item `Y.Map` that flattens the DTO. `type` / `refId` /
 * `createdAt` are reserved top-level keys; every other key is a `state` field
 * (x, y, width, height, zIndex, view, placement, cache, …) so the high-
 * frequency layout registers (x/y/z) are independent CRDT keys — concurrent
 * drags of different items never conflict, and the same item's position is
 * last-writer-wins per field. `cache` is stored as a plain JSON value.
 */
const ITEM_RESERVED_KEYS = new Set(['type', 'refId', 'createdAt']);

// (`uniqueScreenName`, which titled the sheet every app open used to store
// after the app, "Docs", then "Docs 2", went with that sheet on 2026-09-29:
// opening an app never stores a screen now, rule 1 of the Stage algorithm.
// Sheets it named still exist and still read as the app's name, see
// `resolveSheetLabel` in item-label.ts.)

/**
 * A window on the canvas that can be a screen: not a sheet, not docked or
 * filed. What the Stage rules count (lib/workspaces/stage-rules.ts).
 */
function isOnCanvasWindow(it: WorkspaceItemDTO | undefined): it is WorkspaceItemDTO {
  return !!it && it.type !== 'board' && isScreenType(it.type) && isOnCanvas(it.state);
}

/**
 * DESKTOP FULL SCREEN, one record per window this device shows full screen
 * (the Stage algorithm, rules 12 to 17). Local to this tab and never shared:
 * not the document, not the workspace row. The records themselves are never
 * stored; what survives a reload is one id, the full screen ON VIEW, in this
 * tab's own memory (open question 7, answered again on 2026-10-03: a reload
 * keeps the full screen you were looking at; see `syncFullscreenMemory`).
 * The return fields are where the view goes back to on exit, "framed as
 * before" (rule 15).
 */
export type DesktopFullscreenRecord = {
  id: string;
  returnScreenId: string | null;
  returnPrevScreenId: string | null;
  returnViewport: ViewportTransform | null;
};

/**
 * A WINDOW THAT COULD NOT OPEN (fix round 2, 2026-09-30). `addItem` paints a
 * new window at once and asks `resolve-ref` about it before it writes it into
 * the shared doc; after three attempts that time out (15 s each) or fail with
 * a 5xx, the window is withdrawn, which is right, and nothing said so: it
 * simply vanished. The store now keeps one offer, the latest, which the
 * canvas shows as "Couldn't open <name>." with Try again
 * (components/workspace/closed-window-undo.tsx). Local to this tab and never
 * written anywhere. `item` is the withdrawn window as it was painted, for its
 * name; `seq` tells one offer from the next.
 */
export type OpenFailedOffer = { seq: number; item: WorkspaceItemDTO };

/** What `addItem` takes. */
type AddItemInput = Parameters<Store['addItem']>[0];

/**
 * A failure worth trying again: no answer at all (a network drop, the 15 s
 * watchdog's abort, which carries no status) or a 5xx from a pod mid-roll.
 * A 4xx is a definitive answer (no access, a bad ref) that a retry would only
 * repeat. The same test decides `addItem`'s own three attempts.
 */
function isTransientFailure(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  return status === undefined || status >= 500;
}


/**
 * The item state as it goes into the shared Y.Doc: the transient spawn
 * highlight marker stripped out.
 *
 * `justSpawnedAt` is local chrome with a 2s life, and the patch that clears
 * it uses `persist: false` — so anything written into the doc here was never
 * cleaned up again, and every item ever spawned carried a permanent marker
 * that replicated to peers. Leaves `cache` absent when it was absent.
 */
function withoutSpawnMarker(state: WorkspaceItemState): WorkspaceItemState {
  const cache = state.cache as Record<string, unknown> | undefined;
  if (!cache || !('justSpawnedAt' in cache)) return state;
  const { justSpawnedAt: _marker, ...rest } = cache;
  return { ...state, cache: rest as WorkspaceItemState['cache'] };
}

function writeItemToYMap(yItem: Y.Map<unknown>, dto: WorkspaceItemDTO): void {
  yItem.set('type', dto.type);
  yItem.set('refId', dto.refId ?? null);
  yItem.set(
    'createdAt',
    dto.createdAt instanceof Date ? dto.createdAt.toISOString() : String(dto.createdAt ?? ''),
  );
  for (const [k, v] of Object.entries(dto.state)) {
    yItem.set(k, v as unknown);
  }
}

function readItemFromYMap(
  id: string,
  yItem: Y.Map<unknown>,
  workspaceId: string,
): WorkspaceItemDTO {
  const state: Record<string, unknown> = {};
  let type: WorkspaceItemType = 'note';
  let refId: string | null = null;
  let createdAt: string | null = null;
  yItem.forEach((v, k) => {
    if (k === 'type') type = v as WorkspaceItemType;
    else if (k === 'refId') refId = (v as string | null) ?? null;
    else if (k === 'createdAt') createdAt = v as string | null;
    else state[k] = v;
  });
  return {
    id,
    workspaceId,
    type,
    refId,
    state: state as unknown as WorkspaceItemState,
    createdAt: createdAt ? new Date(createdAt) : new Date(),
    updatedAt: new Date(),
  };
}

/**
 * SECURITY: device-permission grants are PER-USER, never shared.
 *
 * The Y.Doc is writable by every Editor, so a sensitive feature
 * (microphone / camera / geo / …) sitting in the SHARED `state.permissions`
 * would let one collaborator hand a widget `allow=microphone` in everyone
 * else's browser — silent capture for any victim who already granted the
 * portal that device. So we (a) STRIP every sensitive feature out of the
 * shared doc projection, and (b) merge back THIS user's own grants from
 * `localStorage`. Non-sensitive tokens pass through untouched.
 *
 * Net effect: the iframe `allow=` reflects what THIS user granted on THIS
 * device, regardless of what any peer wrote into the shared doc.
 */
function applyDeviceGrants(
  item: WorkspaceItemDTO,
  workspaceId: string,
): WorkspaceItemDTO {
  const shared = Array.isArray(item.state?.permissions)
    ? (item.state.permissions as string[])
    : [];
  const local = readLocalWidgetGrants(workspaceId, item.id);
  // Non-sensitive shared tokens + this user's local (sensitive) grants.
  const safe = shared.filter((p) => !isSensitiveFeature(p));
  const next = local.length ? [...new Set([...safe, ...local])] : safe;
  // Avoid churning identity when nothing changed (same length + members).
  if (next.length === shared.length && next.every((p) => shared.includes(p))) {
    return item;
  }
  return { ...item, state: { ...item.state, permissions: next } };
}

/**
 * Currently-armed annotation tool. When non-null, the next click on the
 * workspace pane drops a new annotation item at that flow-space point and
 * clears the pending tool (`one-shot`). `Esc` or re-clicking the same tool
 * cancels.
 *
 * `kind === 'connector'` is special: it doesn't drop a node on click but
 * instead enables React Flow's connection drag (handle → handle). Today the
 * connector tool is implemented by the canvas filtering `nodesConnectable`
 * on this slice's value, so the slice still has to exist as part of the
 * tool palette.
 */
export type PendingAnnotation =
  | { kind: AnnotationKind; color?: AnnotationColor; shape?: AnnotationShape }
  | { kind: 'connector' }
  | null;

type Listener = () => void;

/**
 * First-paint metadata for a content widget. Lives in a dedicated store
 * slice (not on the item's `state.cache`) so the per-item state stays
 * thin and mirrors the row in Postgres. Seeded server-side by
 * `getWorkspaceForUser` to eliminate the "Loading…" flash on revisit
 * — ContentNode reads `useWorkspaceContent(refId)` first and only
 * fetches if the cache misses.
 */
export type WorkspaceContentCache = {
  id: string;
  displayName: string;
  contentType: string;
  mimeType: string;
  thumbnailPath: string | null;
  byteSize: number | null;
  updatedAt: string | null;
};

export type WorkspaceStoreState = {
  workspace: WorkspaceDTO;
  items: WorkspaceItemDTO[];
  /** Number of in-flight network mutations. Drives the SaveIndicator. */
  inFlight: number;
  /** Last network error message (e.g. 'Saving failed — retry'). */
  lastError: string | null;
  /**
   * True when an anonymous visitor is browsing (read-only). The store
   * uses this to suppress AUTOMATIC / background persistence (camera
   * pans, aspect-ratio reflow, z-index housekeeping) so those don't
   * trip the signup gate. Only EXPLICIT edits (drag/resize commit,
   * create, delete, send) reach the network and prompt sign-up.
   */
  anonReadOnly: boolean;
  /**
   * Server-hydrated content metadata keyed by `content.id`. Read-only
   * from the node's perspective — only `seedContent` writes to it.
   * Nodes still fetch the full info row on mount (which fills in
   * per-user fields like share token) but render the cached snapshot
   * during the round-trip so there is no Loading… flash.
   */
  contentCache: Record<string, WorkspaceContentCache>;
  /**
   * `id` of the workspace item currently in "fullscreen takeover" mode,
   * or `null` for the normal canvas. While non-null, the workspace
   * shell hides all chrome (panels, FABs, dock) and renders the
   * matching widget as a viewport-filling overlay. Holding Esc (or the
   * top-edge hover / touch grab handle) reveals the app header as an
   * overlay bar — exit lives on its green window control. See
   * fullscreen-chrome.tsx. Distinct from the fitView-to-this-node
   * action (header double-click) which only adjusts camera.
   */
  fullscreenItemId: string | null;
  /**
   * Screens (window-management v2): which screen — app-like window, see
   * `isScreenType` in `lib/workspaces/screens.ts` — the user's camera is
   * parked on, and the previously parked one (drives the same-digit
   * back-hop on ⌘⇧1..9). Local-only like `fullscreenItemId`; the current
   * id is additionally remembered per-device in localStorage
   * (`lib/workspaces/screen-state.ts`) so a reload lands where the user
   * left off. Never the shared workspace row — your position is yours.
   *
   * `prevScreenId` is "the screen you came from" (rule 9 of the Stage
   * algorithm, 2026-09-29): the last parked id on a DIFFERENT screen.
   * Parking on another window of the same screen (a second app opening on
   * it) leaves it alone, so closing that screen's last window returns you
   * to where you really were.
   */
  currentScreenId: string | null;
  prevScreenId: string | null;
  /**
   * True while the camera is pulled back to the overview (every screen
   * visible, covers over the windows, click to fly in). Local-only.
   */
  overviewMode: boolean;
  /**
   * Desktop full screen (rules 12 to 17): the windows THIS device shows full
   * screen, each on a temporary screen of its own, in the order they went
   * full screen. Local-only beside `overviewMode`: never the shared
   * document. A reload restores the one on view and no other (open question
   * 7, `syncFullscreenMemory`). See `DesktopFullscreenRecord`. Optional in
   * the type's readers: a state built without it is simply "nothing is full
   * screen".
   */
  desktopFullscreen: DesktopFullscreenRecord[];
  /**
   * YOUR VIEW OF THE SCREEN YOU ARE ON, in world units (rules 2 and 3): the
   * camera's world rect, published by the canvas whenever it shows a normal
   * screen on a desktop, and deliberately NOT while Stage or a full-screen
   * screen is up (Discover's overlay leaves the camera where it was). So it
   * is "the last one you were on" from Stage, and "the screen the
   * full-screen app came from" in full screen, which is where rule 2 opens
   * a window then.
   * Null on a handset: a phone places against the 1440 x 900 reference
   * screen instead (rule 18). Local-only.
   */
  screenView: ScreenRect | null;
  /**
   * The phone's in-tab full screen (handoff 2026-09-29, section 4.4; v3
   * section A): the bottom navigation steps aside and the presented app
   * takes its pixels, app bar included. Per DEVICE and local-only, like
   * `overviewMode`; it never enters the shared document, so a phone in full
   * screen changes nothing a laptop sees. Not the browser's Fullscreen API:
   * the address bar stays and there is no camera cutout over the app. Only
   * meaningful while a window is presented; the canvas clears it when
   * nothing is. It survives a reload in the same tab, through the same
   * memory as a desktop's (`syncFullscreenMemory`).
   */
  handsetFullscreen: boolean;
  /**
   * A window to show full screen as soon as the canvas can (2026-10-03, the
   * `/?app=<id>` entry: "the apps must open in full screen mode by default").
   * The link's spawn names the window here; the canvas shows it full screen
   * the way the device does it (a desktop's temporary full-screen screen, a
   * phone's in-tab flag) once the window is on the canvas, and clears this.
   * `at` bounds how long a request waits for a window that never arrives.
   * The same request brings full screen back after a reload
   * (`recallFullscreenOnLoad`). The request itself is local state and is
   * never stored. Optional in the type so a state built without it reads as
   * "nothing asked".
   */
  pendingFullscreen?: { id: string; at: number } | null;
  /**
   * The window that last failed to open, with Try again (`OpenFailedOffer`).
   * Local-only; optional in the type so a state built without it reads as
   * "nothing failed".
   */
  openFailed?: OpenFailedOffer | null;
  /**
   * Currently-armed annotation tool (sticky / shape / text / connector).
   * `null` means the canvas is in the default "pointer" mode where clicks
   * just pan/select. Set by the expanding zoom toolbar (Phase 3) and
   * consumed by the canvas pane-click handler (Phase 2) + connector wiring
   * (Phase 6). One-shot: cleared automatically after the next drop.
   */
  pendingAnnotation: PendingAnnotation;
  /**
   * Last-known flow-space coordinates the user is currently looking at
   * (resolved from `flow.screenToFlowPosition(viewport center)` by
   * `WorkspaceShell` on mount + on every transform update). Used by
   * `addItem` to drop the new widget where the user can see it.
   * `null` until the canvas has measured itself once.
   */
  viewportAnchor: { x: number; y: number } | null;
  /**
   * Visible viewport rectangle in world coordinates — top-left at
   * `(x, y)`, extent `(width, height)`. Same lifecycle as
   * `viewportAnchor`: populated once on canvas mount and updated on
   * every pan/zoom. `addItem` passes this to `findEmptySpace`'s
   * viewport-aware phase so freshly-spawned widgets are placed where
   * the user is currently looking instead of an off-screen origin.
   * `null` until the canvas has measured itself once.
   */
  viewportRect: { x: number; y: number; width: number; height: number } | null;
  /**
   * The canvas in SCREEN pixels (i.e. world units at zoom 1). `viewportRect`
   * is the visible WORLD rect — pixels ÷ zoom — so it balloons when the user
   * is zoomed out. Sizing a window from it meant a spawn while zoomed out
   * produced a window far larger than the screen, opening with its header
   * above the top edge. Window geometry is persistent state and must not
   * depend on where the camera happens to be.
   */
  viewportBox: { width: number; height: number } | null;
};

type Patch = Partial<WorkspaceItemState>;

const DEFAULT_SIZES: Record<WorkspaceItemType, { width: number; height: number }> = {
  // Spawn default for agent widgets — bumped from 360x480 to give the
  // welcome / chat surface more breathing room out of the box. Seeded
  // first-visit sizes are independent (see `lib/workspaces/queries.ts`)
  // and intentionally smaller for the dock-puck layouts.
  agent: { width: 410, height: 580 },
  content: { width: 360, height: 360 },
  post: { width: 320, height: 280 },
  note: { width: 240, height: 160 },
  shape: { width: 180, height: 180 },
  terminal: { width: 720, height: 420 },
  // Three-way shell split. Daemon + SSH inherit the legacy terminal
  // footprint so existing muscle-memory carries over. Python gets a
  // taller default — the REPL needs vertical room for matplotlib
  // figures' scrollback before they fan out as sibling widgets.
  daemon: { width: 720, height: 420 },
  ssh: { width: 720, height: 420 },
  python: { width: 720, height: 480 },
  // Files/Explorer widget — folder tree browser. Compact default;
  // users resize for deeper trees.
  files: { width: 380, height: 440 },
  // Sticky-ish default. The annotation renderer overrides width/height
  // per `cache.kind` (text auto-fits, shape stays a rounded box) — these
  // numbers only matter on the very first drop before the renderer
  // measures itself.
  annotation: { width: 192, height: 144 },
  // Connectors never render as RF nodes (they project to edges), so the
  // size is meaningless. We still record sensible 0×0 here so the
  // backend schema is happy and any accidental code path reading
  // width/height for a connector doesn't blow up.
  connector: { width: 1, height: 1 },
  // Blank screen sheet (Part IV): exactly ONE screenful. The real
  // spawn size comes from the measured viewport in `addItem`; this is
  // only the measured-viewport-less fallback.
  board: { width: 1440, height: 900 },
};

function shallowEqualState(a: WorkspaceItemState, b: WorkspaceItemState): boolean {
  // Compare the full set of keys present on either side. We treat `cache` as
  // a single nested object and compare it shallowly too (one level deep).
  const keys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
  for (const k of keys) {
    if (k === 'cache') continue;
    if ((a as Record<string, unknown>)[k] !== (b as Record<string, unknown>)[k]) {
      return false;
    }
  }
  const ac = (a.cache ?? {}) as Record<string, unknown>;
  const bc = (b.cache ?? {}) as Record<string, unknown>;
  const cacheKeys = new Set<string>([...Object.keys(ac), ...Object.keys(bc)]);
  for (const k of cacheKeys) {
    if (ac[k] !== bc[k]) return false;
  }
  return true;
}

/** Exported for unit tests only — consumers use the hooks below. */
/**
 * How far a spawned CHILD may land from the anchor beside its parent before
 * proximity beats non-overlap. Used only when the viewport is unknown; when we
 * know it, `childDisplacementBudget` below is tighter and is what matters.
 */
const MAX_CHILD_DISPLACEMENT = 2560;

/**
 * The real requirement is not "near" in the abstract, it is VISIBLE: a
 * delegation puck exists so a person can watch the sub-agent work, and one
 * placed outside the pane is no more use than one on another screen.
 *
 * A flat 2560 was the first attempt and it was measured wrong on 2026-09-07:
 * the pane is about 1654 world units at zoom 1, so a puck displaced 1631 units
 * passed the bound and still rendered a full 1000 units off the right edge, and
 * the Stage view labelled parent and child "Screen 1" and "Screen 2" — the
 * original complaint, unfixed. The board sheet an agent sits on is itself an
 * obstacle in `findEmptySpace`, so the spiral ALWAYS exhausts for a puck
 * spawned on a board and the clear-slot escape always fires; the bound is the
 * only thing standing between that and a teleport.
 *
 * So bound by what the person can actually see. Three quarters of the pane
 * leaves room for the puck's own width and the camera's ease, and the floor
 * keeps a very narrow pane from collapsing the budget to nothing.
 */
function childDisplacementBudget(viewport: { width: number } | null | undefined): number {
  if (!viewport?.width) return MAX_CHILD_DISPLACEMENT;
  return Math.max(360, Math.min(MAX_CHILD_DISPLACEMENT, viewport.width * 0.75));
}

export class Store {
  state: WorkspaceStoreState;
  private listeners = new Set<Listener>();
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private workspaceDebounce: ReturnType<typeof setTimeout> | null = null;
  /**
   * Network entrypoint used by every mutating call below. Defaults to
   * the global `fetch`; `WorkspaceStoreProvider` swaps in a wrapped
   * variant that intercepts the structured 401 (`{ code:
   * 'signup_required' }`) and opens the workspace signup dialog
   * before the caller's catch handler runs. Replacing it at the
   * store level means every existing `fetchJSON(...)` callsite picks
   * up the gate for free — no per-action plumbing needed.
   */
  private fetchImpl: typeof fetch =
    typeof fetch !== 'undefined' ? fetch.bind(globalThis) : (async () => new Response(null));

  // ----- Yjs (canvas CRDT) ----------------------------------------------
  // The workspace Y.Doc's `items` map is the source of truth for the canvas.
  // The store mirrors it into `state.items` and writes mutations back.
  private ydoc: Y.Doc | null = null;
  private itemsMap: Y.Map<unknown> | null = null;
  /** Tags our own Y.Doc writes so the observer skips the rebuild for
   *  locally-originated changes (already applied optimistically). */
  private readonly yjsOrigin = { store: true };
  private observer: (() => void) | null = null;
  /**
   * Canvas mutations made while the Y.Doc is NOT attached.
   *
   * The doc is the ONLY durable home for canvas items (`listItems` server-side
   * reads the projected doc snapshot, there is no `workspace_items` row). So a
   * create/delete that lands while `ydoc` is null — the window before
   * `attachYjs`, or after a `detachYjs` on provider teardown/reconnect — used
   * to be applied to local state and then silently dropped: the widget
   * rendered, registered with the command bus and heartbeated into tab
   * presence, but no peer, no fresh tab and no MCP `workspace_widgets_invoke`
   * could ever see it, and it vanished on the next resync. Queue those ops
   * instead and flush them the moment a doc attaches.
   *
   * Keyed by item id — last write wins, so an add+remove collapses to the
   * remove and we never replay a stale intermediate state.
   */
  private pendingYjsOps = new Map<
    string,
    { kind: 'set'; dto: WorkspaceItemDTO } | { kind: 'delete' }
  >();
  /**
   * Items THIS tab added in the last few seconds, id → added-at. A rebuild
   * from the doc (`syncFromYjs`) replaces `state.items` wholesale with the
   * map's content — and a just-spawned item can be missing from that map
   * through no fault of its own (the write went to a doc the provider then
   * swapped/tore down, or the authoritative first sync landed between the
   * optimistic insert and the doc write). For one-shot callers (deep-link
   * spawn — the smoke's open-chat gate) that silent drop is permanent, so
   * rebuilds CARRY THESE OVER and re-write them into the live doc instead.
   * A genuine remote delete of an item this same tab added within the
   * window is indistinguishable — and re-adding is the safer failure mode.
   */
  private recentLocalAdds = new Map<string, number>();
  private static readonly RECENT_ADD_WINDOW_MS = 20_000;
  /** SSR / first-paint items, retained so onYjsSynced can seed an empty doc. */
  private readonly initialItems: WorkspaceItemDTO[];
  private seeded = false;
  /**
   * THE ITEMS THE WORKSPACE LOADED WITH (fix round 2, 2026-09-30): the ids of
   * the server's items when this page opened the workspace, taken once and
   * never changed. Stage's one-time tidy (components/workspace/stage-tidy.tsx)
   * offers only sheets in this set, because it exists for the OLD empty
   * screens, the ones the retired app-open default minted; it had offered to
   * remove a person's brand-new screen the moment they made it. Anything
   * made after the page loaded, here or by a peer, on any path (New screen,
   * a drop on the New screen tile, a rename, rule 10's "Screen N", an old
   * client's app-named sheet), is not in it.
   *
   * A snapshot rather than the `createdAt` the items carry, on purpose:
   * `createdAt` is stamped by the creating device's own clock (a peer whose
   * clock runs behind would date a new sheet into the past), and
   * `readItemFromYMap` reads an item without the key as created NOW, on every
   * rebuild. The snapshot needs no clock and no writer's cooperation, and its
   * one failure is harmless: an old sheet missing from the server's list at
   * load (a projection that lagged) is simply not offered this time.
   */
  readonly loadedItemIds: ReadonlySet<string>;
  /** The input of the window that last failed to open (`openFailed`), for Try again. */
  private failedOpen: { seq: number; input: AddItemInput } | null = null;
  private failedOpenSeq = 0;

  constructor(initial: WorkspaceStoreState) {
    // Strip shared-doc sensitive grants + merge this user's local grants on
    // the FIRST render too (SSR strips, client hydration also merges) — so the
    // initial iframe `allow=` is never poisoned by a peer's shared-doc write
    // during the window before the Y.Doc syncs.
    const wsId = initial.workspace.id;
    const items = initial.items.map((it) => applyDeviceGrants(it, wsId));
    this.state = { ...initial, items };
    this.initialItems = items;
    this.loadedItemIds = new Set(items.map((it) => it.id));
  }

  /** Inject a custom `fetch` (e.g. the signup-gate wrapped variant). */
  setFetch(fn: typeof fetch): void {
    this.fetchImpl = fn;
  }

  // ----- Yjs canvas doc -------------------------------------------------

  /** Wire the workspace Y.Doc as the source of truth for canvas items. */
  attachYjs(doc: Y.Doc): void {
    if (this.ydoc === doc) return;
    this.detachYjs();
    this.ydoc = doc;
    const map = doc.getMap('items');
    this.itemsMap = map;
    const handler = (_events: unknown, txn: Y.Transaction) => {
      // Our own writes were already applied to local state optimistically;
      // only rebuild for REMOTE changes (other peers / pod recovery).
      if (txn.origin === this.yjsOrigin) return;
      this.syncFromYjs();
    };
    map.observeDeep(handler);
    // Re-project when THIS user's local device grants change (same tab via the
    // custom event, other tabs via `storage`) so the iframe `allow=` updates
    // without a shared-doc write.
    const offGrants = subscribeLocalWidgetGrants(() => this.syncFromYjs());
    this.observer = () => {
      map.unobserveDeep(handler);
      offGrants();
    };
    // Replay anything created/removed while no doc was attached BEFORE the
    // rebuild below — otherwise syncFromYjs would project the doc over the top
    // of those items and drop them from the canvas.
    this.flushPendingYjsOps();
    // Re-attach after the provider already synced: adopt the live doc now.
    // Otherwise keep showing initialItems until onYjsSynced fires.
    if (map.size > 0) this.syncFromYjs();
  }

  detachYjs(): void {
    if (this.observer) {
      this.observer();
      this.observer = null;
    }
    this.ydoc = null;
    this.itemsMap = null;
  }

  /**
   * Called once the provider completes its first sync with the pod. Seeds an
   * empty doc from the SSR items (first-visit / migration seed) then renders
   * from the doc. Reader writes are dropped pod-side, so only an editor can
   * actually seed — no double-seed race across peers.
   *
   * ONLY A DOC NOBODY HAS WRITTEN IS SEEDED (2026-10-01). "Empty" used to be
   * `map.size === 0`, which is also what a doc looks like after its LAST
   * window was closed. The SSR items are read from the projected snapshot
   * (`content_room_docs.state_snapshot`), which the collab pod writes a beat
   * after the doc changes, so a page load inside that lag found the closed
   * window in the stale snapshot and an empty doc, and seeded the window back
   * under its old id: close your only window, reload, and it returned. The
   * deploy-gating smoke does exactly that between `remove-agent-widget` and
   * `discover-search`, and then failed `preview-add-to-workspace` three runs
   * out of three, because "Add to workspace" focuses a window that is already
   * there instead of adding one. It surfaced when opening an app stopped
   * minting a `board` sheet under it (rule 1 in `addItem`, 2026-09-29): until
   * then that sheet outlived the window and the doc was never empty.
   *
   * So the test is whether the `items` map has EVER held an entry. Yjs keeps
   * a deleted key in the map's `_map` as a tombstone, and the tombstone
   * survives the pod's persistence and a fresh client's first sync, so no
   * marker of our own is needed in the doc and a delete by an older client or
   * a peer counts too. Writes to other parts of the doc (canvas comments) do
   * not. Pinned in tests/unit/workspaces/store-yjs-emptied-doc-not-reseeded.test.ts,
   * including the Yjs behaviour itself.
   */
  onYjsSynced(): void {
    if (!this.itemsMap || this.seeded) return;
    this.seeded = true;
    // A create can race the first sync (attachYjs fires with an empty doc, the
    // user drops a widget, the pod's state arrives a beat later). Replay first
    // so the seed check below sees a doc that already holds it.
    this.flushPendingYjsOps();
    const map = this.itemsMap;
    const neverWritten = map.size === 0 && map._map.size === 0;
    if (neverWritten && this.initialItems.length > 0 && !this.state.anonReadOnly) {
      this.ydoc!.transact(() => {
        for (const dto of this.initialItems) {
          if (dto.id.startsWith('temp-')) continue;
          const yItem = new Y.Map();
          writeItemToYMap(yItem, dto);
          map.set(dto.id, yItem);
        }
      }, this.yjsOrigin);
    }
    this.syncFromYjs();
  }

  /** Rebuild `state.items` from the Y.Map (remote changes / initial adopt). */
  private syncFromYjs(): void {
    const map = this.itemsMap;
    if (!map) return;
    const wsId = this.state.workspace.id;
    const items: WorkspaceItemDTO[] = [];
    map.forEach((yItem, id) => {
      if (yItem instanceof Y.Map) {
        items.push(applyDeviceGrants(readItemFromYMap(id, yItem, wsId), wsId));
      }
    });
    // Carry over this tab's just-added items that the map doesn't hold yet
    // (see `recentLocalAdds`) and re-write them into the live doc so the
    // next rebuild — and every peer — converges on them. Without this, a
    // rebuild racing a fresh spawn silently unmounts the new widget: the
    // one-shot deep-link spawn (smoke `open-chat`) never recovers from that.
    const now = Date.now();
    for (const [id, at] of this.recentLocalAdds) {
      if (now - at > Store.RECENT_ADD_WINDOW_MS) {
        this.recentLocalAdds.delete(id);
        continue;
      }
      if (map.has(id)) continue;
      const live = this.state.items.find((it) => it.id === id);
      if (!live) continue;
      console.warn(
        `[workspace] doc rebuild dropped just-added item ${id} (${live.type}) — carrying over`,
      );
      items.push(live);
      this.setYItem(live);
    }
    const before = this.state.items;
    const fullscreenBefore = this.fullscreenList();
    const parked = { current: this.state.currentScreenId, prev: this.state.prevScreenId };
    this.set({ items });
    // A PEER closed, minimized or filed a window (rules 9 to 11): "anyone who
    // was on it returns to the screen they came from". Only the view moves
    // here; the "Screen N" sheet rule 10 keeps is the peer's to store (it
    // runs the same rule on its own change), so a sheet is never minted by
    // every device that watched it happen. The cheap check first: most
    // rebuilds are drags and edits, which take no window off the canvas.
    this.pruneFullscreen();
    const byId = new Map(items.map((it) => [it.id, it]));
    if (before.some((it) => isOnCanvasWindow(it) && !isOnCanvasWindow(byId.get(it.id)))) {
      this.settleLeave(before, { local: false, fullscreenBefore, parked });
    }
  }

  /**
   * Write an item into the shared doc, or queue it when no doc is attached
   * yet. Never silently drops: everything queued here is replayed by
   * `flushPendingYjsOps` as soon as `attachYjs` wires a doc.
   */
  private setYItem(dto: WorkspaceItemDTO): void {
    if (this.ydoc && this.itemsMap) {
      const yItem = new Y.Map();
      writeItemToYMap(yItem, dto);
      this.ydoc.transact(() => this.itemsMap!.set(dto.id, yItem), this.yjsOrigin);
      return;
    }
    this.pendingYjsOps.set(dto.id, { kind: 'set', dto });
  }

  /**
   * Record a freshly-mounted item in the SERVER's projection of this canvas
   * immediately, instead of leaving the server blind to it until the collab
   * pod's debounced store catches up.
   *
   * WHY (2026-09-06 — "a brand-new app 404s exactly once, then works forever").
   * `addItem` mints the item id here in the browser and writes it straight into
   * the Y.Doc; the comment on that line explains why (the canvas is a CRDT, so
   * there is nothing to settle a temp id against). The consequence nobody had
   * traced until today is that the ONLY server-readable record of a canvas item
   * is `content_room_docs.state_snapshot -> 'items'`, and until now the only
   * writer of that column was the pod's store (`apps/collab/hocuspocus.ts`:
   * debounce 2s, maxDebounce 10s). An app's very first `host.fn.invoke` lands
   * inside that window: `POST /api/widgets/[id]/fn/[name]` resolves the instance
   * from that projection, found nothing, and both fell back to the platform's
   * system agent (a 404 naming @robutler for an app that never asked for it)
   * and left `widgetScope.appId` / `instanceOwnerId` unset, so owner
   * attribution missed the first call as well. Every LATER load worked, which
   * is exactly why it read as a first-run bug. Agent-opened apps hit it because
   * `open_app` mounts through the tab RPC and so runs THIS code; MCP
   * `widget_instantiate` never did, because it goes through the server.
   *
   * FIRE AND FORGET, on purpose. `addItem` is on the interaction path and the
   * whole reason the id is minted locally is that nobody should wait for a
   * round-trip before their app appears. The item is already painted, already
   * in the doc and already durable when this is sent; the request only decides
   * whether the server learns about it in 30ms or in 2s. A bounded client-side
   * retry on `instance_not_ready` still covers the residual case, so a failure
   * here degrades to the old timing rather than to a broken app — which is why
   * it is logged, not surfaced.
   *
   * Note the `void … .catch(…)` shape: attaching the handler to the promise we
   * void is the whole point. The trap this codebase has hit before is voiding
   * an outer promise while the catch sits on an inner one, which leaves the
   * outer rejection unhandled.
   */
  private projectItemEarly(dto: WorkspaceItemDTO): void {
    // An anonymous viewer cannot write to the workspace at all — the route
    // would answer `signup_required`, and the wrapped fetch would pop the
    // signup dialog for an add the visitor never made.
    if (this.state.anonReadOnly) return;
    void this.fetchJSON<{ ok: true; projected?: boolean }>(`/api/workspaces/resolve-ref`, {
      method: 'POST',
      // Same watchdog as the blocking call above: a HUNG response rejects
      // nothing on its own, and this promise's only job is to reach its catch.
      signal: AbortSignal.timeout(15_000),
      body: JSON.stringify({
        workspaceId: dto.workspaceId,
        type: dto.type,
        refId: dto.refId ?? null,
        // The state EXACTLY as it went into the Y.Doc one line earlier, so the
        // projection cannot describe the item differently from the doc.
        item: {
          id: dto.id,
          state: dto.state,
          createdAt:
            dto.createdAt instanceof Date
              ? dto.createdAt.toISOString()
              : String(dto.createdAt ?? ''),
        },
      }),
    }).catch((err) => {
      console.warn(
        `[workspace] early projection of item ${dto.id} (${dto.type}) failed — ` +
          `the collab pod will project it within ~2s; the app's first fn call may retry:`,
        err,
      );
    });
  }

  /** Delete an item from the shared doc, or queue the delete. See `setYItem`. */
  private deleteYItem(itemId: string): void {
    if (this.ydoc && this.itemsMap) {
      this.ydoc.transact(() => this.itemsMap!.delete(itemId), this.yjsOrigin);
      return;
    }
    this.pendingYjsOps.set(itemId, { kind: 'delete' });
  }

  /**
   * Replay canvas mutations that were made while the doc was detached. Runs
   * BEFORE any `syncFromYjs`, so a rebuild from the doc can never wipe a
   * pending local item off the canvas.
   */
  private flushPendingYjsOps(): void {
    if (!this.ydoc || !this.itemsMap || this.pendingYjsOps.size === 0) return;
    const ops = [...this.pendingYjsOps];
    this.pendingYjsOps.clear();
    const map = this.itemsMap;
    this.ydoc.transact(() => {
      for (const [id, op] of ops) {
        if (op.kind === 'delete') {
          map.delete(id);
        } else {
          const yItem = new Y.Map();
          writeItemToYMap(yItem, op.dto);
          map.set(id, yItem);
        }
      }
    }, this.yjsOrigin);
  }

  /** Mutate a single item's Y.Map under our own origin. Returns false when the
   *  doc isn't attached or the item is gone. */
  private withYItem(itemId: string, fn: (yItem: Y.Map<unknown>) => void): boolean {
    if (!this.ydoc || !this.itemsMap) return false;
    const yItem = this.itemsMap.get(itemId);
    if (!(yItem instanceof Y.Map)) return false;
    this.ydoc.transact(() => fn(yItem), this.yjsOrigin);
    return true;
  }

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSnapshot = () => this.state;

  private emit() {
    for (const fn of this.listeners) fn();
  }

  private set(next: Partial<WorkspaceStoreState>) {
    this.state = { ...this.state, ...next };
    this.syncFullscreenMemory();
    this.emit();
  }

  private bumpInFlight(delta: number) {
    this.set({ inFlight: Math.max(0, this.state.inFlight + delta) });
  }

  private async fetchJSON<T>(url: string, init: RequestInit): Promise<T> {
    const res = await this.fetchImpl(url, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init.headers ?? {}) },
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      // Carry the status so callers can tell a definitive answer (4xx —
      // ACL denial, bad ref) from a transient one (5xx) when deciding
      // whether a retry is safe.
      const err = new Error(text || `Request failed: ${res.status}`) as Error & {
        status?: number;
      };
      err.status = res.status;
      throw err;
    }
    return res.json() as Promise<T>;
  }

  // ----- workspace ------------------------------------------------------

  setWorkspaceCamera(cameraX: number, cameraY: number, cameraZoom: number) {
    this.set({
      workspace: { ...this.state.workspace, cameraX, cameraY, cameraZoom },
    });
    if (this.workspaceDebounce) clearTimeout(this.workspaceDebounce);
    this.workspaceDebounce = setTimeout(() => {
      this.persistWorkspace({ cameraX, cameraY, cameraZoom }).catch(() => {});
    }, 600);
  }

  async renameWorkspace(name: string) {
    const prev = this.state.workspace;
    this.set({ workspace: { ...prev, name } });
    try {
      await this.persistWorkspace({ name });
    } catch (err) {
      this.set({ workspace: prev, lastError: (err as Error).message });
    }
  }

  /**
   * Re-seed server-owned workspace fields after an RSC refresh. Only the
   * name is taken: the camera is client-owned and live, so copying the whole
   * DTO would yank the viewport back on every `router.refresh()`.
   */
  syncWorkspaceFromServer(next: WorkspaceDTO) {
    const prev = this.state.workspace;
    if (next.id !== prev.id) {
      this.set({ workspace: next });
      return;
    }
    if (next.name === prev.name) return;
    this.set({ workspace: { ...prev, name: next.name } });
  }

  async updateWorkspaceSettings(settings: Partial<WorkspaceSettings>) {
    const prev = this.state.workspace;
    const merged = { ...(prev.settings ?? {}), ...settings };
    this.set({ workspace: { ...prev, settings: merged } });
    try {
      await this.persistWorkspace({ settings: merged });
    } catch (err) {
      this.set({ workspace: prev, lastError: (err as Error).message });
    }
  }

  private async persistWorkspace(patch: Partial<WorkspaceDTO>) {
    // Camera / name persistence is always background housekeeping. An
    // anon visitor can't own this workspace, so never hit the network
    // (and never pop the signup gate) for it — the optimistic local
    // update already applied above.
    if (this.state.anonReadOnly) return;
    this.bumpInFlight(1);
    try {
      const data = await this.fetchJSON<{ workspace: WorkspaceDTO }>(
        `/api/workspaces/${this.state.workspace.id}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      );
      this.set({ workspace: data.workspace, lastError: null });
    } catch (err) {
      this.set({ lastError: (err as Error).message });
      throw err;
    } finally {
      this.bumpInFlight(-1);
    }
  }

  // ----- fullscreen -----------------------------------------------------
  // Local-only state — never persisted. The store treats it the same
  // way it treats `inFlight`: a UI hint that doesn't survive a reload.
  // We also drop fullscreen on item removal so a closed widget can't
  // leave the workspace stranded in a hidden state.
  setFullscreenItem(id: string | null) {
    if (this.state.fullscreenItemId === id) return;
    this.set({ fullscreenItemId: id });
  }

  // ----- screens --------------------------------------------------------
  // Local-only, like fullscreen: the screen the camera is parked on and
  // whether the overview is up. The current screen id is also written to
  // per-device localStorage so a reload restores the user's own position
  // (the shared workspace camera stays a first-visit fallback only).
  setCurrentScreen(id: string | null) {
    const prev = this.state.currentScreenId;
    if (prev === id) return;
    // Keep the last DIFFERENT screen, so the same-digit chord can hop back
    // and a screen that empties can return you to where you came from
    // (rule 9); clearing to null (item removed) preserves the old prev.
    // Since 2026-09-29 "different" means a different SCREEN, not merely a
    // different parked window: several windows share a screen now, and
    // parking on the second app you opened on it is not a move.
    let prevScreenId = this.state.prevScreenId;
    if (prev) {
      if (!id) {
        // cleared: prev stays
      } else {
        const entries = displayScreenEntries(this.state.items, this.fullscreenIds());
        const from = entryOfItem(entries, prev);
        const to = entryOfItem(entries, id);
        if (!from || !to || from.id !== to.id) prevScreenId = prev;
      }
    }
    this.set({ currentScreenId: id, prevScreenId });
    saveCurrentScreen(this.state.workspace.id, id);
  }

  /**
   * Put the parked and previous screens back exactly, the way leaving full
   * screen does (rule 15: "returns the view to the screen the app came from,
   * framed as before"): going there through `setCurrentScreen` would record
   * the full-screen screen as where you came from.
   */
  restoreScreens(current: string | null, prev: string | null) {
    if (this.state.currentScreenId === current && this.state.prevScreenId === prev) return;
    this.set({ currentScreenId: current, prevScreenId: prev });
    saveCurrentScreen(this.state.workspace.id, current);
  }

  setOverviewMode(on: boolean) {
    if (this.state.overviewMode === on) return;
    this.set({ overviewMode: on });
  }

  // The phone's in-tab full screen: local-only, see the state field.
  setHandsetFullscreen(on: boolean) {
    if (this.state.handsetFullscreen === on) return;
    this.set({ handsetFullscreen: on });
  }

  // ----- full screen on open (the `/?app=<id>` entry) -----------------------
  // The spawn action names the window; the canvas does the rest (see the
  // `pendingFullscreen` field). The request is local state.
  requestFullscreenOnOpen(id: string) {
    this.set({ pendingFullscreen: { id, at: Date.now() } });
  }

  /**
   * The request is settled, shown or lapsed. Either way what this tab
   * remembers is made to match what it shows now, so a window that never
   * came back (closed since, minimized by a peer) is forgotten rather than
   * asked for again on every reload.
   */
  clearPendingFullscreen() {
    if (!this.state.pendingFullscreen) return;
    this.set({ pendingFullscreen: null });
    this.syncFullscreenMemory(true);
  }

  // ----- full screen survives a reload (2026-10-03) -------------------------
  // The owner, on the app entry: "not on refresh, ... menu should exit
  // fullscreen". This tab remembers ONE id, the window that is full screen
  // and on view (lib/workspaces/screen-state.ts, sessionStorage, shared with
  // no one). It is written here and nowhere else, from the state itself, so
  // every way out forgets it without having to know about it: Exit full
  // screen in the menu, the bar's exit, Esc, Minimize, Close, a peer closing
  // the window, the view moving to another screen, a phone with nothing left
  // to present.

  /** The id this store last wrote to the tab's memory; null when none. */
  private fullscreenRemembered: string | null = null;

  /** The window this tab shows full screen and has on view, or null. */
  private fullscreenOnView(): string | null {
    const s = this.state;
    // A phone's flag belongs to whatever it presents (only a phone sets it).
    if (s.handsetFullscreen) return presentedWindowId(s.items, s.currentScreenId);
    const cur = s.currentScreenId;
    return cur && this.fullscreenList().some((r) => r.id === cur) ? cur : null;
  }

  /**
   * Keep the tab's memory equal to the full screen on view. Runs after every
   * state change and costs nothing while nothing changed. A fresh store has
   * written nothing and shows nothing, so it leaves alone the id a reload is
   * about to ask for; `settled` is the one caller that knows the request is
   * over and writes regardless.
   */
  private syncFullscreenMemory(settled = false) {
    const onView = this.fullscreenOnView();
    if (!settled && onView === this.fullscreenRemembered) return;
    this.fullscreenRemembered = onView;
    rememberFullscreen(this.state.workspace.id, onView);
  }

  /**
   * After a reload: ask for the full screen this tab was showing, through the
   * same request an app link makes. The canvas calls it once when it mounts.
   * A request already waiting is a link's own and is newer, so it stays.
   */
  recallFullscreenOnLoad() {
    if (this.state.pendingFullscreen) return;
    const id = recallFullscreen(this.state.workspace.id);
    if (id) this.set({ pendingFullscreen: { id, at: Date.now() } });
  }

  // ----- desktop full screen (rules 12 to 17) ------------------------------
  // Local-only, like `overviewMode`: which windows this device shows full
  // screen. Nothing here touches an item or the shared document, and nothing
  // here writes storage (the tab's memory is `syncFullscreenMemory`'s alone);
  // drawing (a display-only size under the app bar), the camera and the
  // browser's own full screen are the canvas's (workspace-canvas.tsx).

  /** The records, tolerant of a state built before the field existed. */
  private fullscreenList(): DesktopFullscreenRecord[] {
    return this.state.desktopFullscreen ?? [];
  }

  private fullscreenIds(): string[] {
    return this.fullscreenList().map((r) => r.id);
  }

  /**
   * Show `id` full screen on this device (rule 12), on a temporary screen of
   * its own after the one it came from (rule 13), and park on it. `back` is
   * where exit returns: the parked screens and the camera as they are now.
   * Several windows may be full screen at once (rule 17). Returns false
   * when `id` is not a window on the canvas.
   */
  enterDesktopFullscreen(
    id: string,
    back: { screenId: string | null; prevScreenId: string | null; viewport: ViewportTransform | null },
  ): boolean {
    const it = this.state.items.find((x) => x.id === id);
    if (!isOnCanvasWindow(it)) return false;
    const list = this.fullscreenList();
    if (!list.some((r) => r.id === id)) {
      this.set({
        desktopFullscreen: [
          ...list,
          { id, returnScreenId: back.screenId, returnPrevScreenId: back.prevScreenId, returnViewport: back.viewport },
        ],
      });
    }
    this.setCurrentScreen(id);
    return true;
  }

  /**
   * Stop showing `id` full screen (rule 15): its temporary screen goes. The
   * record comes back so the caller can return the view "framed as before";
   * null when `id` was not full screen.
   */
  exitDesktopFullscreen(id: string): DesktopFullscreenRecord | null {
    const list = this.fullscreenList();
    const record = list.find((r) => r.id === id) ?? null;
    if (!record) return null;
    this.set({ desktopFullscreen: list.filter((r) => r.id !== id) });
    return record;
  }

  /**
   * Drop the records of windows that are no longer on the canvas (closed,
   * minimized, filed, here or by a peer). A full-screen window that goes
   * takes its temporary screen with it (rule 16); where the view goes is the
   * leave rules' call (`settleLeave`).
   */
  private pruneFullscreen(): void {
    const list = this.fullscreenList();
    if (list.length === 0) return;
    const next = list.filter((r) => isOnCanvasWindow(this.state.items.find((x) => x.id === r.id)));
    if (next.length !== list.length) this.set({ desktopFullscreen: next });
  }

  /**
   * Your view of the screen you are on, published by the canvas on every
   * camera move (see `screenView`). Stored WITHOUT an emit: only `addItem`
   * reads it, synchronously, and a render per camera frame for a value no
   * component draws would be pure churn.
   */
  setScreenView(rect: ScreenRect | null) {
    const cur = this.state.screenView ?? null;
    if (
      (cur === null && rect === null) ||
      (cur &&
        rect &&
        cur.x === rect.x &&
        cur.y === rect.y &&
        cur.width === rect.width &&
        cur.height === rect.height)
    ) {
      return;
    }
    this.state = { ...this.state, screenView: rect };
  }

  // ----- content cache --------------------------------------------------
  // Per-content-id snapshot of name/mime/etc, seeded from the server on
  // page load and topped up by any client-side fetches that come back
  // with fresher data. Writes never trigger a re-render of an item
  // whose `cache` already matches the new value (cheap reference check).
  seedContent(meta: WorkspaceContentCache) {
    const prev = this.state.contentCache[meta.id];
    if (
      prev &&
      prev.displayName === meta.displayName &&
      prev.contentType === meta.contentType &&
      prev.mimeType === meta.mimeType &&
      prev.thumbnailPath === meta.thumbnailPath &&
      prev.byteSize === meta.byteSize &&
      prev.updatedAt === meta.updatedAt
    ) {
      return;
    }
    this.set({
      contentCache: { ...this.state.contentCache, [meta.id]: meta },
    });
  }

  // ----- annotation tool ------------------------------------------------
  // Local-only one-shot: when set, the next pane click drops an annotation
  // at that point (Phase 2/3 wiring) and the slice is automatically reset.
  // Esc / re-clicking the tool resets it too. Connector mode keeps the
  // slice set while the user drags a handle → handle line so React Flow's
  // `nodesConnectable` stays true.
  setViewportAnchor(anchor: { x: number; y: number } | null) {
    const cur = this.state.viewportAnchor;
    if (
      (cur === null && anchor === null) ||
      (cur && anchor && cur.x === anchor.x && cur.y === anchor.y)
    ) {
      return;
    }
    this.set({ viewportAnchor: anchor });
  }

  setViewportRect(
    rect: { x: number; y: number; width: number; height: number } | null,
  ) {
    const cur = this.state.viewportRect;
    if (
      (cur === null && rect === null) ||
      (cur &&
        rect &&
        cur.x === rect.x &&
        cur.y === rect.y &&
        cur.width === rect.width &&
        cur.height === rect.height)
    ) {
      return;
    }
    this.set({ viewportRect: rect });
  }

  setViewportBox(box: { width: number; height: number } | null) {
    const cur = this.state.viewportBox;
    if (
      (cur === null && box === null) ||
      (cur && box && cur.width === box.width && cur.height === box.height)
    ) {
      return;
    }
    this.set({ viewportBox: box });
  }

  setPendingAnnotation(pending: PendingAnnotation) {
    const a = this.state.pendingAnnotation;
    const b = pending;
    if (a === b) return;
    if (
      a &&
      b &&
      a.kind === b.kind &&
      ('color' in a ? a.color : undefined) === ('color' in b ? b.color : undefined) &&
      ('shape' in a ? a.shape : undefined) === ('shape' in b ? b.shape : undefined)
    ) {
      return;
    }
    this.set({ pendingAnnotation: pending });
  }

  // ----- items ----------------------------------------------------------

  defaultStateFor(type: WorkspaceItemType, atScreen?: { x: number; y: number }): WorkspaceItemState {
    const size = DEFAULT_SIZES[type];
    const offset = (this.state.items.length % 8) * 24;
    return {
      x: (atScreen?.x ?? 80) + offset,
      y: (atScreen?.y ?? 80) + offset,
      width: size.width,
      height: size.height,
      // New agent widgets land on the welcome state. Welcome IS the
      // chat experience — `ChatView` renders the agent's own draft
      // greeting (or the persisted chat once `chatId` is set), so the
      // user sees the agent's defined welcome rather than custom
      // widget chrome. No state button is active in this resting state.
      view: type === 'agent' ? 'welcome' : undefined,
      // Terminal nodes start with no target — the in-node picker will
      // populate it before the WS gateway will accept a connection.
      // Daemon + SSH share the same `state.terminal` shape and need
      // an identical "no target picked yet" sentinel.
      ...(type === 'terminal' || type === 'daemon' || type === 'ssh'
        ? { terminal: { target: null } }
        : {}),
      // Python widgets are auto-allowed for agent control (sandboxed
      // inside the user's own tab). Daemon + SSH default to false —
      // the user must explicitly opt in from the settings dialog.
      ...(type === 'python' ? { allowAgentControl: true } : {}),
      ...(type === 'daemon' || type === 'ssh' ? { allowAgentControl: false } : {}),
      // Annotations default to a sticky note unless the caller patches
      // `cache` with a different kind. Connectors get a minimal shell —
      // the caller is responsible for filling in `from` + `to` since
      // those are mandatory for the edge projection to fire.
      ...(type === 'annotation'
        ? { cache: { kind: 'sticky', text: '', color: 'yellow' as const } }
        : {}),
      ...(type === 'connector' ? { cache: {} } : {}),
    };
  }

  countByRef(refId: string): number {
    return this.state.items.filter((it) => it.refId === refId).length;
  }

  findFirstByRef(refId: string): WorkspaceItemDTO | undefined {
    return this.state.items.find((it) => it.refId === refId && isOnCanvas(it.state));
  }

  /**
   * Place a new widget relative to an existing parent and link them with an
   * edge. The placement selector lets callers choose where the child lands
   * relative to the parent:
   *
   *   - `'right'`  (default) — sibling figure / sub-agent appearing to the
   *     right of the parent. Used by `spawnFromAgent`, matplotlib display
   *     channel, and `present.*` agent tools.
   *   - `'below'`  — direct vertical descendant. Used for delegation
   *     subtrees so the chain reads top-to-bottom.
   *   - `'left'`   — symmetric with `right`, used by future planning views.
   *   - `'above'`  — mirror of `below`; uncommon, available for completeness.
   *
   * Returns null when the parent is no longer on the canvas.
   */
  async spawnChild(input: {
    parentItemId: string;
    type: WorkspaceItemType;
    refId?: string | null;
    state?: Partial<WorkspaceItemState>;
    placement?: 'right' | 'left' | 'above' | 'below';
  }): Promise<WorkspaceItemDTO | null> {
    const parent = this.state.items.find((it) => it.id === input.parentItemId);
    if (!parent) return null;
    const placement = input.placement ?? 'right';
    // Use the child's ACTUAL size when the caller supplies one (delegate
    // pucks pass a 64x92 collapsed size). Falling back to
    // `DEFAULT_SIZES[type]` (e.g. 410x580 for an agent) made the anchor
    // and the collision box ~6x too wide, so a puck meant for "just to
    // the right" landed far away / on the wrong side once
    // `findEmptySpace` spiralled around the oversized box.
    const baseSize =
      input.state && typeof input.state.width === 'number' && typeof input.state.height === 'number'
        ? { width: input.state.width, height: input.state.height }
        : DEFAULT_SIZES[input.type];
    const explicitX = input.state && typeof input.state.x === 'number';
    const explicitY = input.state && typeof input.state.y === 'number';
    let x = (input.state?.x ?? 0) as number;
    let y = (input.state?.y ?? 0) as number;
    if (!explicitX || !explicitY) {
      const gap = 48;
      // Delegate/sub-agent pucks read best spawned a bit further out to
      // the right of the parent dialog (extra breathing room) and
      // vertically centred on it, rather than hugging its top edge.
      const rightGap = 140;
      const anchor = (() => {
        switch (placement) {
          case 'left':
            return {
              x: parent.state.x - gap - baseSize.width / 2,
              y: parent.state.y + parent.state.height / 2,
            };
          case 'above':
            return {
              x: parent.state.x + baseSize.width / 2,
              y: parent.state.y - gap - baseSize.height / 2,
            };
          case 'below':
            return {
              x: parent.state.x + baseSize.width / 2,
              y: parent.state.y + parent.state.height + gap + baseSize.height / 2,
            };
          case 'right':
          default:
            return {
              x: parent.state.x + parent.state.width + rightGap + baseSize.width / 2,
              y: parent.state.y + parent.state.height / 2,
            };
        }
      })();
      const placed = findEmptySpace(
        this.state.items
          .filter((it) => it.id !== input.parentItemId)
          // Docked items live in the dock bar, not on the canvas.
          .filter((it) => isOnCanvas(it.state))
          .map((it) => it.state),
        baseSize,
        anchor,
        {
          viewport: this.state.viewportRect ?? undefined,
          // A CHILD belongs beside its parent. Without this bound the
          // exhausted-spiral fallback slides past the right edge of the
          // bounding box of everything on the canvas, so one board sheet
          // far out to the right teleported a delegation puck to roughly
          // x=12840, off-screen, while the user watched the delegation
          // happen (2026-09-07). Past the bound the anchor wins and a
          // slight overlap is accepted: an overlapping puck can be seen
          // and dragged, a puck on another screen cannot.
          maxDisplacement: childDisplacementBudget(this.state.viewportRect),
        },
      );
      x = placed.x;
      y = placed.y;
    }
    return this.addItem({
      type: input.type,
      refId: input.refId,
      state: {
        ...(input.state ?? {}),
        x,
        y,
        parentItemId: input.parentItemId,
      },
    });
  }

  /**
   * @deprecated Use `spawnChild({ placement: 'right' })`. Kept as a
   * stable alias for existing call-sites (chat-driven agent spawn,
   * content sibling spawn) while the codebase migrates to the more
   * general API.
   */
  spawnFromAgent(input: {
    parentItemId: string;
    type: WorkspaceItemType;
    refId?: string | null;
    state?: Partial<WorkspaceItemState>;
  }): Promise<WorkspaceItemDTO | null> {
    return this.spawnChild({ ...input, placement: 'right' });
  }

  /**
   * RULE 2 OF THE STAGE ALGORITHM: the screen a window opens on, and your
   * view of it, which rules 3 to 5 place against (`placeOpenWindow`).
   *
   *   - The screen your view is on: the parked screen (`currentScreenId`).
   *     From Stage or Discover that is still the last one you were on. In
   *     full screen the parked id is the full-screen window, and the STORED
   *     screen list resolves it to the screen it came from, which is where
   *     rule 2 opens the window (the canvas then takes the view there).
   *   - Your view of it: the live camera the canvas publishes (`screenView`)
   *     when it shows that screen; otherwise the view a desktop camera framed
   *     on it would have, on the sizing box (`screenViewRect`). A phone has
   *     no live desktop view and always takes the second, on the 1440 x 900
   *     reference box lane C publishes as `viewportBox` (rule 18), so a
   *     window opened on a phone is the size a laptop would give it.
   *   - With nothing parked, the view when it frames a screen (the P2 rule
   *     this replaces: never mint a window between screens), else the
   *     nearest screen; in an empty workspace, wherever your view is.
   *
   * The windows are that screen's members as STORED, so the "taken" test of
   * rule 4 sees what every device sees, a window this device shows full
   * screen included. Null when nothing is measured yet.
   */
  private openTarget(): { view: ScreenRect; windows: Array<{ x: number; y: number }> } | null {
    const items = this.state.items;
    const box = this.state.viewportBox;
    const live = this.state.screenView ?? null;
    const vr = this.state.viewportRect;
    const entries = orderScreenEntries(items);
    const usable = (r: ScreenRect | null | undefined): r is ScreenRect => !!r && r.width > 40 && r.height > 40;
    const framedView = (entry: (typeof entries)[number]): ScreenRect | null =>
      box && box.width > 40 && box.height > 40 ? screenViewRect(entry, items, box) : null;
    let entry = entryOfItem(entries, this.state.currentScreenId);
    let view: ScreenRect | null = null;
    if (entry) {
      view = usable(live) && rectsIntersect(live, entry.rect) ? live : (framedView(entry) ?? live ?? null);
    } else if (entries.length > 0) {
      const seen = usable(live) ? live : vr;
      if (usable(seen)) {
        const pick = pickSettleScreen(entries.map((e) => ({ id: e.id, rect: e.rect })), seen);
        const picked = pick ? (entries.find((e) => e.id === pick.id) ?? null) : null;
        if (picked) {
          entry = picked;
          view =
            usable(live) && pick!.coverage >= SETTLE_MIN_COVERAGE
              ? live
              : (framedView(picked) ?? seen);
        }
      }
    } else if (usable(live)) {
      view = live;
    } else if (usable(vr)) {
      // No live desktop view (a phone, or nothing framed yet): a sizing-box
      // sized view centred on what the camera shows.
      view =
        box && box.width > 40 && box.height > 40
          ? {
              x: vr.x + vr.width / 2 - box.width / 2,
              y: vr.y + vr.height / 2 - box.height / 2,
              width: box.width,
              height: box.height,
            }
          : vr;
    }
    if (!usable(view)) return null;
    const windows = entry
      ? entry.memberIds
          .map((id) => items.find((it) => it.id === id))
          .filter(isOnCanvasWindow)
          .map((it) => ({ x: it.state.x, y: it.state.y }))
      : [];
    return { view, windows };
  }

  async addItem(input: {
    type: WorkspaceItemType;
    refId?: string | null;
    state?: Partial<WorkspaceItemState>;
    /**
     * Placement mode for collision avoidance. Defaults to `'viewport'`.
     *
     * - `'viewport'` (default): the caller has no opinion about where
     *   the widget should land — `findEmptySpace` will bias the spawn
     *   inside the user's current viewport, spiralling outward only
     *   when nothing inside fits. Used by search-everything, agent
     *   spawn, the FAB.
     * - `'cursor'`: the caller passed `state.x` / `state.y` to mean
     *   "drop it HERE if at all possible" (paste, HTML5 drop). We
     *   still run collision detection so the widget slides off the
     *   exact pixel when it would land on top of an existing one,
     *   but viewport-fit is skipped so the widget stays near the
     *   user's pointer even when the cursor is at the edge of the
     *   pane.
     */
    placement?: 'viewport' | 'cursor';
    /**
     * Accepted and IGNORED since 2026-09-29. It chose between the per-device
     * "Where new apps open" setting's two answers; the setting is retired
     * (the Stage algorithm, open question 1, answered with the proposal) and
     * every window opens on the screen you are on (rules 1 to 8). Callers
     * that compose a layout (the first-run starters pass `'current'`) keep
     * getting exactly that.
     */
    screen?: 'new' | 'current';
    /**
     * WHO asked for this window. Defaults to `'user'`.
     *
     * Rule 8 of the Stage algorithm (2026-09-29, open question 2 answered
     * with the proposal): an agent's open lands exactly where a person's
     * does, on top of the screen the person is on, with the spawn highlight,
     * but WITHOUT taking the keyboard: the person may be typing in the chat
     * that asked for it, and the focus stays there until they click the new
     * app. A person's open takes the keyboard (rule 6). Before today an
     * agent's open was tiled into the free room of the screen and fell back
     * to a screen of its own (2026-09-06); `findScreenTileSlot` in
     * screens.ts keeps that tiling for the owner's final word.
     */
    openedBy?: 'user' | 'agent';
    /**
     * The local registry's size for this app, used as the DEFAULT to fit
     * from — not as a pin. Passing it through `state.width/height` counted
     * as "the caller has an opinion", which skipped the expand pass and made
     * every catalog spawn land at exactly its declared size on any display.
     */
    sizeHint?: { size?: Size; minSize?: Size | null; maxSize?: Size | null };
    /**
     * Bring the new item into view and play the spawn highlight. Default
     * true. Scenery passes false: a "Screen N" sheet the leave rules store
     * (rule 10) or the rename path stores must neither pulse nor pull the
     * camera.
     */
    focus?: boolean;
    /**
     * A new SCREEN (a `board`) that should be named "Screen N" after the
     * number it takes on Stage (`newScreenName`, fix round 2), worked out
     * here once the sheet's place is final. Ignored when the caller gives the
     * sheet a title of its own (a rename) and for anything but a board.
     */
    nameScreen?: boolean;
  }): Promise<WorkspaceItemDTO> {
    // Client-generated stable id — the canvas is a CRDT, so there is no
    // server round-trip to settle a temp id against.
    const realId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `c-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
    const baseState = this.defaultStateFor(input.type);
    const state: WorkspaceItemState = { ...baseState, ...(input.state ?? {}) };
    const placement = input.placement ?? 'viewport';
    if (input.sizeHint?.size && !(input.state && typeof input.state.width === 'number')) {
      state.width = input.sizeHint.size.width;
      state.height = input.sizeHint.size.height;
    }
    const callerSetX = input.state && typeof input.state.x === 'number';
    const callerSetY = input.state && typeof input.state.y === 'number';
    const callerSetW = input.state && typeof input.state.width === 'number';
    const callerSetH = input.state && typeof input.state.height === 'number';
    // Apps must never open as a cramped tile. When the caller pinned no
    // size and the app declared none of its own (the declared size, if any,
    // arrives below via the resolve-ref cache and overrides this), size the
    // instance to comfortably fill the current viewport rather than the
    // generic 360² content default. Gated to app-like content (a widget row
    // or an ADR-0022 project folder) so files/images keep their own sizing.
    const box = this.state.viewportBox;
    const cacheContentType = (input.state?.cache as { contentType?: string } | undefined)
      ?.contentType;
    const appLike =
      (input.type === 'content' &&
        (cacheContentType === 'widget' ||
          cacheContentType === 'folder' ||
          // An "Open in" pin mounts an EDITOR around the file (Writer on a
          // docx, Video Studio on an mp4) — that's an app window, not a
          // media tile, so it opens at app size.
          !!input.state?.openWith)) ||
      // Boards are screens too — they take a lattice slot like an app
      // window so a new board lands one clean screen away.
      input.type === 'board';
    if (appLike && !callerSetW && !callerSetH) {
      const vr = this.state.viewportRect;
      if (input.type === 'board') {
        // Part IV: a board is the backing sheet of a BLANK SCREEN — exactly
        // one LATTICE CELL, never scrolled, never resized. Sizing it to the
        // viewport made the same screen a different size on every device,
        // which is what let screens overlap. See SCREEN_CELL.
        state.width = SCREEN_CELL.width;
        state.height = SCREEN_CELL.height;
      } else if (box && box.width > 40 && box.height > 40) {
        // Sized from the SCREEN box, never the zoom-dependent world rect —
        // see `viewportBox`. An app must open fitting the viewport the user
        // is actually looking at.
        //
        // This is the size the window is PAINTED at, one network round-trip
        // before `resolve-ref` returns and the declared-size best-fit below
        // replaces it. So it uses the same screenful geometry as that
        // best-fit (viewport minus the expand margin, stopping at the screen
        // cell): the two agreeing is what makes an open one steady frame.
        // The old `min(1280, box.width * 0.72)` disagreed on both counts —
        // it opened at two thirds of the screen and then visibly resized.
        state.width = Math.max(
          560,
          Math.min(SCREEN_CELL.width, box.width - EXPAND_MARGIN * 2),
        );
        state.height = Math.max(
          420,
          Math.min(SCREEN_CELL.height, box.height - EXPAND_MARGIN * 2),
        );
      } else {
        // No measured viewport yet — a roomy default still beats 360².
        state.width = Math.max(state.width, 880);
        state.height = Math.max(state.height, 600);
      }
    }
    // Where a window that opens on a screen was placed (rules 2 to 5): the
    // view and the windows it was placed against, and the size it keeps when
    // it has no fill of its own. The declared bounds `resolve-ref` returns
    // re-run the same placement below; null for every other kind of add.
    let openedOn: {
      view: ScreenRect;
      windows: Array<{ x: number; y: number }>;
      fixed: Size | null;
    } | null = null;
    if (placement === 'cursor' && callerSetX && callerSetY) {
      // Cursor-anchored spawn (paste / drop): honour the caller's
      // (x, y) and only slide off if that exact spot overlaps an
      // existing non-docked widget. Skips the viewport-fit pass so
      // we don't yank the widget back into the centre when the user
      // dropped near the edge.
      const placed = findEmptySpace(
        this.state.items
          .filter((it) => isOnCanvas(it.state))
          .map((it) => it.state),
        { width: state.width, height: state.height },
        { x: state.x, y: state.y },
        { preferExactAnchor: true },
      );
      state.x = placed.x;
      state.y = placed.y;
    } else if (!callerSetX && !callerSetY) {
      const obstacles = this.state.items
        // Docked items live in the dock bar, not on the canvas
        // proper — exclude them from the collision check so
        // spawns don't bias away from a permanently-docked tile.
        .filter((it) => isOnCanvas(it.state))
        .map((it) => it.state);
      const vr = this.state.viewportRect;
      // WINDOWS OPEN ON TOP OF THE SCREEN YOU ARE ON (the Stage algorithm,
      // rules 1 to 8, 2026-09-29). One helper, `placeOpenWindow`
      // (open-placement.ts), for a person's open and an agent's alike: it
      // fills your view of that screen minus 32 px, and when that spot is
      // taken steps 28 px and sticks out 12 px past the window it covers, so
      // every window keeps a strip the mouse can click (rules 3 to 5). It
      // replaced two branches: the "Open in a new screen" default, which put
      // the app in the free lattice cell nearest the camera AND stored a
      // sheet named after it there (the owner's 26 screens, 11 of them empty
      // and named after apps long closed), and a current-screen cascade that
      // stepped by the window COUNT and snapped to 24 px (steps of 24 or 48).
      // Nothing else moves, no window is resized and no screen is made or
      // stored (rules 1 and 7); the zIndex below puts the new window on top
      // (rule 6).
      //
      // A window with no fill of its own (a chat, a shell, the files browser,
      // or one the caller sized) keeps its size: it is "held smaller", so it
      // sits in the middle and cascades 28 px keeping its size. Boards keep
      // the lattice slot below: a new SCREEN lands one clean cell away.
      const opensOnCurrentScreen =
        input.type !== 'board' &&
        (appLike ||
          input.type === 'agent' ||
          input.type === 'terminal' ||
          input.type === 'daemon' ||
          input.type === 'ssh' ||
          input.type === 'python' ||
          input.type === 'files');
      const target = opensOnCurrentScreen ? this.openTarget() : null;
      if (target) {
        const fixed: Size | null =
          appLike && !callerSetW && !callerSetH ? null : { width: state.width, height: state.height };
        const spot = placeOpenWindow({
          view: target.view,
          windows: target.windows,
          min: fixed ?? input.sizeHint?.minSize ?? null,
          max: fixed ?? input.sizeHint?.maxSize ?? null,
        });
        state.x = spot.x;
        state.y = spot.y;
        state.width = spot.width;
        state.height = spot.height;
        openedOn = { view: target.view, windows: target.windows, fixed };
      } else if (input.type === 'board' && vr && vr.width > 40 && vr.height > 40) {
        // New screens tile on the screen-sized macro-grid — one clean
        // lattice cell away from the neighbours. See findScreenGridSlot.
        const placed = findScreenGridSlot(
          obstacles,
          { width: state.width, height: state.height },
          vr,
        );
        state.x = placed.x;
        state.y = placed.y;
      } else {
        // Default viewport-bias path: drop into the nearest
        // viewport-fitting empty space (resolved via `viewportAnchor`
        // + `viewportRect`). When no anchor exists yet (e.g. before the
        // canvas has measured itself) we still MUST run the collision
        // pass, anchored on the default top-left bias from
        // `defaultStateFor` — skipping it stacked every early spawn at
        // the literal same (40, 40) rect.
        const anchor = this.state.viewportAnchor ?? {
          x: state.x + state.width / 2,
          y: state.y + state.height / 2,
        };
        const placed = findEmptySpace(
          obstacles,
          { width: state.width, height: state.height },
          anchor,
          { viewport: this.state.viewportRect ?? undefined },
        );
        state.x = placed.x;
        state.y = placed.y;
      }
    }
    // A new screen's default name, now that its place is final: "Screen N"
    // for the number it takes on Stage (`newScreenName`, fix round 2). A
    // title the caller gave (a person's rename) is kept as it is.
    if (input.type === 'board' && input.nameScreen) {
      const cache = (state.cache ?? {}) as Record<string, unknown>;
      if (typeof cache.title !== 'string' || !cache.title.trim()) {
        state.cache = {
          ...cache,
          title: newScreenName(
            this.state.items,
            { x: state.x, y: state.y, width: state.width, height: state.height },
            realId,
          ),
        } as WorkspaceItemState['cache'];
      }
    }
    // Auto-stack: a freshly-spawned widget should always render on top
    // of everything else already on the canvas, otherwise the camera
    // pan from `focusOnSpawn` reveals a node that may be sitting under
    // an older content / annotation widget at the same coordinates.
    // `findEmptySpace` minimises overlap but cannot guarantee a clear
    // slot on a dense canvas (the smoke test's owner workspace tends
    // to accumulate hundreds of items across runs). Skipped when the
    // caller pinned `zIndex` explicitly — agent-driven spawns that
    // need to slot behind their host (e.g. delegation pucks) opt out
    // by passing their own value.
    const callerSetZ = input.state && typeof input.state.zIndex === 'number';
    if (!callerSetZ) {
      const maxZ = this.state.items.reduce(
        (acc, it) => Math.max(acc, it.state.zIndex ?? 0),
        0,
      );
      state.zIndex = maxZ + 1;
    }
    // Stamp a transient "just spawned" marker on the cache so the
    // canvas can apply a brief highlight pulse to draw the user's
    // eye to where the widget landed. Cleared on a 2s timeout
    // below so re-renders don't replay the animation.
    //
    // ONLY on a focused add. `focus: false` means scenery — above all the
    // backing sheet a new screen mints for the window that opened it — and
    // scenery must not pulse: a sheet is a full lattice CELL, so it painted
    // a second, screen-sized purple ring around the window the user actually
    // opened, and every app open showed two rings. The clear-timeout below
    // is gated on the same flag, so an unfocused add would also have kept
    // the marker forever.
    const spawnedAt = Date.now();
    if (input.focus !== false) {
      state.cache = {
        ...(state.cache ?? {}),
        justSpawnedAt: spawnedAt,
      } as WorkspaceItemState['cache'];
    }
    const optimistic: WorkspaceItemDTO = {
      id: realId,
      workspaceId: this.state.workspace.id,
      type: input.type,
      refId: input.refId ?? null,
      state,
      createdAt: new Date(),
      updatedAt: new Date(),
    };
    // Optimistic local render first — the canvas updates instantly.
    this.set({ items: [...this.state.items, optimistic] });
    // Shield the fresh add from a racing doc rebuild (see syncFromYjs).
    this.recentLocalAdds.set(realId, Date.now());
    this.bumpInFlight(1);
    try {
      // Server-side ACL + first-paint cache. There is no per-item Postgres
      // row in the CRDT model, so this hook is what keeps refId access
      // server-enforced before we broadcast the item into the shared doc.
      //
      // Bounded retry on transient failures (network drop, 5xx from a pod
      // mid-roll). resolve-ref is a read-only resolution, so re-sending is
      // safe. Definitive answers (4xx: ACL denial, bad ref) still throw
      // immediately — the catch below removes the optimistic item, which is
      // the correct outcome for a ref the user genuinely can't attach. A
      // one-shot caller (deep-link spawn) previously lost the item forever
      // on a single blip here.
      let cache: Record<string, unknown> | null = null;
      for (let attempt = 0; ; attempt++) {
        try {
          ({ cache } = await this.fetchJSON<{
            ok: true;
            cache: Record<string, unknown> | null;
          }>(`/api/workspaces/resolve-ref`, {
            method: 'POST',
            // Per-attempt watchdog: a HUNG response (socket open, reply never
            // arriving — pod mid-roll) rejects nothing on its own and froze
            // one-shot callers forever (dev 233-pre, 2026-08-11). A timeout
            // rejection carries no `.status`, so it takes the transient
            // branch below and retries like any network drop.
            signal: AbortSignal.timeout(15_000),
            body: JSON.stringify({
              workspaceId: this.state.workspace.id,
              type: input.type,
              refId: input.refId ?? null,
            }),
          }));
          break;
        } catch (err) {
          if (!isTransientFailure(err) || attempt >= 2) throw err;
          await new Promise((r) => setTimeout(r, 500 * (attempt + 1)));
        }
      }
      if (cache) {
        state.cache = { ...(state.cache ?? {}), ...cache } as WorkspaceItemState['cache'];
        // DECLARED size constraints (registry/manifest, surfaced by resolve-ref)
        // drive a screen-aware best-fit — but only when the caller had no size
        // opinion of its own. Respects min/max and the "min > screen → keep the
        // app's default size and let the canvas fit/zoom" rule.
        //
        // Runs for every APP-LIKE spawn, including one whose row declares
        // nothing. Gating on `decl.size || decl.minSize || decl.maxSize`
        // stranded exactly the rows that carry no size metadata — a private
        // remix writes `metadata: { bundle }` and nothing else — on the
        // pre-size above with no expand pass, so a remix of an app opened
        // visibly smaller than the app it was remixed from.
        // Boards are excluded: a screen sheet is exactly one lattice cell
        // (see SCREEN_CELL), never a best-fit result.
        const decl = cache as { size?: Size; minSize?: Size; maxSize?: Size };
        const fitsToScreen =
          input.type !== 'board' &&
          (appLike || !!decl.size || !!decl.minSize || !!decl.maxSize);
        if (openedOn) {
          // RULE 3 AGAIN, now that the app's DECLARED bounds are known: the
          // same view and the same windows, so the spot is the one rules 4
          // and 5 give a window of the real size (a max-size app moves to
          // the middle, a larger minimum can end the cascade sooner). The
          // bounds come from whichever source has them: the server's
          // metadata once a reseed carries them, the local registry until
          // then. A fixed-size window keeps what it was given.
          if (!openedOn.fixed) {
            const spot = placeOpenWindow({
              view: openedOn.view,
              windows: openedOn.windows,
              min: decl.minSize ?? input.sizeHint?.minSize ?? null,
              max: decl.maxSize ?? input.sizeHint?.maxSize ?? null,
            });
            state.x = spot.x;
            state.y = spot.y;
            state.width = spot.width;
            state.height = spot.height;
          }
        } else if (!callerSetW && !callerSetH && fitsToScreen) {
          const fit = bestFitSize({
            default: decl.size ?? input.sizeHint?.size ?? { width: state.width, height: state.height },
            // The bounds come from whichever source has them: the server's
            // metadata once a reseed carries them, the local registry until
            // then. Without this an app's declared ceiling wouldn't apply
            // until the widgets are re-seeded.
            min: decl.minSize ?? input.sizeHint?.minSize ?? null,
            max: decl.maxSize ?? input.sizeHint?.maxSize ?? null,
            // The physical screen, not the world rect: "fit the viewport the
            // user is looking at" must not change with the zoom level.
            viewport: this.state.viewportBox ?? null,
            // Apps open EXPANDED — the declared default states the app's
            // proportions, not a ceiling, so it must not cap the window.
            // Only an app's own declared `maxSize` stops the fill.
            expand: true,
            // ...and stop at the SCREEN, so the window is the same size on
            // every display and covers its cell exactly.
            screen: SCREEN_CELL,
          });
          state.width = fit.width;
          state.height = fit.height;
        }
        this.set({
          items: this.state.items.map((it) =>
            it.id === realId ? { ...it, state } : it,
          ),
        });
      }
      // Durably write into the workspace Y.Doc — shared with peers, persisted
      // to content_room_docs by the pod. Own-origin so our observer skips the
      // rebuild (we already applied it optimistically above). Queued instead
      // when no doc is attached yet, so the item can never end up local-only.
      //
      // The spawn marker is LOCAL chrome and never becomes durable CRDT
      // state: the 2s clear below patches with `persist: false`, so anything
      // written here would have stayed in the doc for the life of the
      // workspace and travelled to every peer.
      const durable: WorkspaceItemDTO = { ...optimistic, state: withoutSpawnMarker(state) };
      this.setYItem(durable);
      // ...and tell the SERVER the item exists, now, rather than leaving it
      // invisible to every server-side reader until the collab pod's debounced
      // store runs. See `projectItemEarly`.
      this.projectItemEarly(durable);
      // RULE 1: OPENING AN APP NEVER STORES A SCREEN. Until 2026-09-29 an app
      // that opened on a screen of its own also minted a `board` sheet under
      // it here, titled after the app ("Writer", then "Writer 2"), and
      // closing the app removed only the window: the owner's Stage had 26
      // screens, 11 of them empty and named after apps long gone. The screen
      // an app opens on is now worked out from the windows (an auto screen,
      // `orderScreenEntries`) and goes when they do. A person who wants a
      // screen of their own makes one (New screen in Stage), and the sheets
      // already stored stay kept screens: nothing here reads or removes them.
      this.set({ lastError: null });
      emitWorkspaceItemsChanged();
      if (typeof window !== 'undefined' && input.focus !== false) {
        // `keyboard` (rule 6): a person's open takes the keyboard, so the
        // canvas focuses the new window's app once it is there. An agent's
        // open never does (rule 8), and neither does any add that is not a
        // window opening on the screen (a paste, a drop, a spawned figure).
        window.dispatchEvent(
          new CustomEvent('ws:focus-item', {
            detail: { itemId: realId, keyboard: !!openedOn && input.openedBy !== 'agent' },
          }),
        );
        // Drop the transient justSpawnedAt marker after the highlight
        // animation finishes so subsequent renders don't replay it. Local-only
        // (persist:false) so the highlight never broadcasts to peers.
        setTimeout(() => {
          const live = this.state.items.find((it) => it.id === realId);
          if (!live) {
            // Explicit removes clear `recentLocalAdds`; if the id is still
            // tracked, the item vanished through no user action — the
            // silent one-shot-spawn killer (smoke open-chat). Name it.
            if (this.recentLocalAdds.has(realId)) {
              console.warn(
                `[workspace] item ${realId} (${input.type}) vanished within 2s of add — doc rebuild race?`,
              );
            }
            return;
          }
          if ((live.state.cache?.justSpawnedAt as number | undefined) !== spawnedAt) return;
          const nextCache = { ...(live.state.cache ?? {}) } as Record<string, unknown>;
          delete nextCache.justSpawnedAt;
          this.patchItemState(realId, { cache: nextCache as WorkspaceItemState['cache'] }, {
            persist: false,
          });
        }, 2000);
      }
      return optimistic;
    } catch (err) {
      // Rolled back — the carry-over shield must not resurrect it.
      this.recentLocalAdds.delete(realId);
      // SAY SO (fix round 2, 2026-09-30; `OpenFailedOffer`). The withdrawal
      // stays exactly as it was: the window never reached the shared doc
      // (`setYItem` runs only after resolve-ref answered), so nothing
      // half-open is left for anyone. What is new is one plain offer for a
      // WINDOW someone asked to open (not scenery such as a sheet, which adds
      // with `focus: false`) that gave up on a failure worth trying again; a
      // definitive refusal (a 4xx) would only be refused again, and stays
      // with its caller. The latest failure replaces an earlier offer.
      const opensWindow =
        input.type !== 'board' && isScreenType(input.type) && input.focus !== false;
      const offer: OpenFailedOffer | null =
        opensWindow && isTransientFailure(err) ? { seq: ++this.failedOpenSeq, item: optimistic } : null;
      if (offer) this.failedOpen = { seq: offer.seq, input };
      this.set({
        items: this.state.items.filter((it) => it.id !== realId),
        lastError: (err as Error).message,
        ...(offer ? { openFailed: offer } : {}),
      });
      throw err;
    } finally {
      this.bumpInFlight(-1);
    }
  }

  /**
   * Merge an item created out-of-band — an agent via MCP
   * `widget_instantiate`, or one of the user's other tabs — that arrived
   * over the workspace bus (`kind: 'item.created'`). Idempotent by id, so an
   * echo of our OWN optimistic add (same row, same id) is a no-op and there
   * is no double-render. We deliberately do NOT move the camera: a remote add
   * must not hijack the viewer's view.
   */
  mergeRemoteItem(dto: WorkspaceItemDTO): void {
    // Server-side actors (an agent via MCP `widget_instantiate`) still create
    // items through `createItem` + the Redis workspace bus, not the client
    // Y.Doc. Bridge that into the CRDT: write the bus row into the workspace
    // doc so it renders + persists. Idempotent by id across peers (only the
    // first writer's set sticks; the rest are identical no-ops). We do NOT tag
    // it own-origin, so the observer rebuilds and renders it.
    //
    // KNOWN GAP: if NO client is connected when the agent acts, the item lands
    // only in workspace_items (not the doc) until a client re-seeds — true
    // server→CRDT writes (pod-side apply) are the follow-up.
    if (!dto || typeof dto.id !== 'string') return;
    if (!this.state.workspace || dto.workspaceId !== this.state.workspace.id) return;
    if (this.itemsMap?.has(dto.id)) return;
    // No doc attached yet → queue rather than drop, closing the same
    // local-only hole `setYItem` guards for interactive adds. NOTE: unlike our
    // own writes this must NOT be own-origin, so the observer rebuilds and
    // renders it; flushPendingYjsOps replays under yjsOrigin, so we follow it
    // with an explicit projection.
    if (!this.ydoc || !this.itemsMap) {
      this.pendingYjsOps.set(dto.id, { kind: 'set', dto });
      return;
    }
    const yItem = new Y.Map();
    writeItemToYMap(yItem, dto);
    this.ydoc.transact(() => this.itemsMap!.set(dto.id, yItem));
    emitWorkspaceItemsChanged();
  }

  /**
   * Patch a widget's state.
   *
   * Local store update happens synchronously (so React Flow's controlled
   * `nodes` prop reflects the in-progress drag/resize). The remote PATCH
   * is debounced and can be skipped entirely with `persist: false` —
   * useful for high-frequency intermediate frames where only the final
   * gesture state is worth a network round-trip. Calling once with
   * `persist: true` after the gesture ends schedules the actual save.
   */
  patchItemState(
    itemId: string,
    patch: Patch,
    opts?: { debounceMs?: number; persist?: boolean; background?: boolean },
  ) {
    const prev = this.state.items.find((it) => it.id === itemId);
    if (!prev) return;
    const before = this.state.items;
    const fullscreenBefore = this.fullscreenList();
    const parked = { current: this.state.currentScreenId, prev: this.state.prevScreenId };
    const merged: WorkspaceItemState = {
      ...prev.state,
      ...patch,
      cache: { ...(prev.state.cache ?? {}), ...(patch.cache ?? {}) },
    };
    // Skip if the patch is a no-op. Critical for stopping feedback loops
    // between React Flow's measured dimensions and our persisted state —
    // without this, every render of an unchanged node would re-emit and
    // trigger another PATCH cycle.
    const isNoop = shallowEqualState(prev.state, merged);
    if (!isNoop) {
      this.set({
        items: this.state.items.map((it) =>
          it.id === itemId ? { ...it, state: merged } : it,
        ),
      });
    }

    // RULES 9 TO 11: minimizing a window to the dock (or filing it) takes it
    // off its screen, and it may have been the last window there. Only a
    // committed change that takes a window OFF the canvas counts: a drag
    // carries its screen with it, and restoring from the dock adds a window.
    // Read from the merged state rather than from `patch.placement`, so the
    // legacy `docked` flag counts too. A `persist: false` frame is not a
    // leave; every caller that minimizes or files (node-frame.tsx, the rail
    // dock, url-actions.ts) commits in one patch, so no intermediate frame
    // ever takes a window off the canvas ahead of its commit.
    if (opts?.persist !== false && isOnCanvasWindow(prev) && !isOnCanvas(merged)) {
      this.pruneFullscreen();
      this.settleLeave(before, { local: true, fullscreenBefore, parked });
    }

    // `persist: false` = local-only (transient UI: the just-spawned highlight,
    // intermediate frames we don't want to broadcast). Never touches the doc.
    if (opts?.persist === false) return;
    // Background housekeeping (aspect-ratio reflow, z-index stacking, auto-fit)
    // is not an intentional edit; an anon visitor can never persist anyway, so
    // skip the doc write (their reader connection would drop it pod-side).
    if (opts?.background && this.state.anonReadOnly) return;

    // Write the changed fields into the item's Y.Map, diffing each field
    // against the DOC's current value (NOT local state) and only writing
    // the ones that actually differ.
    //
    // Why not gate on the local `isNoop` (as we used to with an
    // `if (isNoop) return` here): a drag / resize streams its intermediate
    // frames with `persist:false`, which update LOCAL state but deliberately
    // skip the doc. By the time the gesture's FINAL commit lands, the patch
    // already matches local state (`isNoop === true`) even though the doc is
    // still stale — so the early return dropped that commit and size /
    // position changes never reached other tabs. Diffing against the doc
    // fixes that, and still breaks the React-Flow measured-dimension feedback
    // loop: an unchanged field is simply never re-set, so nothing is
    // broadcast (an empty Y.Transaction emits no update).
    this.withYItem(itemId, (yItem) => {
      for (const [k, v] of Object.entries(patch)) {
        if (k === 'cache') {
          const prevCache =
            (yItem.get('cache') as Record<string, unknown> | undefined) ?? {};
          const nextCache = { ...prevCache, ...(patch.cache ?? {}) };
          const cacheChanged =
            Object.keys(nextCache).length !== Object.keys(prevCache).length ||
            Object.entries(nextCache).some(([ck, cv]) => prevCache[ck] !== cv);
          if (cacheChanged) yItem.set('cache', nextCache);
        } else {
          // Normalise `undefined` → `null` so "clear this field" intent
          // (e.g. chatId: undefined for "Start a new chat") actually lands.
          const nv = v === undefined ? null : (v as unknown);
          if (yItem.get(k) !== nv) yItem.set(k, nv);
        }
      }
    });
  }

  async removeItem(itemId: string) {
    // An explicit local remove must beat the rebuild carry-over — without
    // this, closing a just-spawned widget could resurrect it (syncFromYjs).
    this.recentLocalAdds.delete(itemId);
    const before = this.state.items;
    const fullscreenBefore = this.fullscreenList();
    const parked = { current: this.state.currentScreenId, prev: this.state.prevScreenId };
    const optimistic = this.state.items.filter((it) => it.id !== itemId);
    // Clear fullscreen if the removed item was the one taking over the viewport
    // — otherwise the workspace would be stranded with no widget to render.
    if (this.state.fullscreenItemId === itemId) {
      this.set({ items: optimistic, fullscreenItemId: null });
    } else {
      this.set({ items: optimistic });
    }
    // A removed screen can't stay "current" — drop the parked position so
    // the next pan settles fresh (prev survives for the back-hop).
    if (this.state.currentScreenId === itemId) {
      this.set({ currentScreenId: null });
      saveCurrentScreen(this.state.workspace.id, null);
    }
    if (this.state.prevScreenId === itemId) {
      this.set({ prevScreenId: null });
    }
    // Delete from the shared workspace doc (own-origin → observer skips the
    // rebuild; the pod persists the deletion to content_room_docs). NOTE: we
    // intentionally do NOT delete the item's saved content/project folder here
    // — removing a widget from the canvas keeps its content in My Library.
    this.deleteYItem(itemId);
    emitWorkspaceItemsChanged();
    // RULES 9 TO 11 (and 16: a full-screen window closed takes its temporary
    // screen with it). Removing a sheet is a person's act on a kept screen
    // (Stage's X, the tidy) and runs the same rules: the view leaves a screen
    // that is gone.
    this.pruneFullscreen();
    this.settleLeave(before, { local: true, fullscreenBefore, parked });
  }

  /**
   * LEAVING A SCREEN (the Stage algorithm, rules 9 to 11; the pure decision
   * is `decideLeave` in lib/workspaces/stage-rules.ts). Runs after a change
   * took windows off the canvas (closed, minimized, filed) or off their
   * screen (`settleAfterMove`).
   *
   *   - The screen the view was on is gone (its last window left an auto
   *     screen): the view returns to the screen you came from, else the
   *     neighbour on the left, else the right, through the `ws:screen`
   *     chokepoint's `frame` kind (no raise, no un-dock). The close Undo
   *     brings the window back, and with it the screen (rule 9).
   *   - Things a person made are still on that screen (rule 10): it becomes
   *     a kept screen, a "Screen N" sheet stored at its outline, the shape
   *     the rename path and the New screen tile have always stored. ONLY for
   *     a change made on THIS device (`local`): a peer runs the same rule on
   *     its own change, so the sheet is minted once, and a device that only
   *     watched keeps its view where the notes are.
   *   - The screen stays but the parked window left it: park on a window
   *     still there, without moving the camera.
   *   - A kept screen stays, empty or not (rule 11).
   *
   * `parked` is the parked and previous ids as they were BEFORE the change
   * (`removeItem` clears a removed parked id first).
   */
  private settleLeave(
    before: WorkspaceItemDTO[],
    opts: {
      local: boolean;
      movedIds?: string[];
      fullscreenBefore: DesktopFullscreenRecord[];
      parked: { current: string | null; prev: string | null };
    },
  ): void {
    const outcome = decideLeave({
      before,
      after: this.state.items,
      fullscreenBefore: opts.fullscreenBefore.map((r) => r.id),
      fullscreenAfter: this.fullscreenIds(),
      fullscreenReturns: new Map(opts.fullscreenBefore.map((r) => [r.id, r.returnScreenId])),
      currentScreenId: opts.parked.current,
      prevScreenId: opts.parked.prev,
      movedIds: opts.movedIds,
    });
    let keptSheet: Promise<WorkspaceItemDTO> | null = null;
    if (opts.local && !this.state.anonReadOnly && outcome.keep.length > 0) {
      const minZ = this.state.items.reduce((acc, it) => Math.min(acc, it.state.zIndex ?? 0), 0);
      for (const k of outcome.keep) {
        // "Screen N" for the number the kept screen has on Stage
        // (`nameScreen`, fix round 2): it keeps the place the auto screen had.
        const sheet = this.addItem({
          type: 'board',
          refId: null,
          focus: false,
          nameScreen: true,
          state: {
            x: Math.round(k.outline.x),
            y: Math.round(k.outline.y),
            width: k.outline.width,
            height: k.outline.height,
            zIndex: minZ - 1,
          },
        });
        sheet.catch(() => {
          /* the add rolled itself back and reported it; the notes stay put */
        });
        if (outcome.park.kind === 'keep' && outcome.park.entryId === k.entryId) keptSheet = sheet;
      }
    }
    const park = outcome.park;
    if (park.kind === 'repark') {
      // Same screen, another window on it: where you came from is unchanged.
      this.restoreScreens(park.id, opts.parked.prev);
    } else if (park.kind === 'return') {
      // The screen you were on is gone, so there is no "back" to it: the
      // same-digit back-hop must not un-minimize the window that emptied it.
      this.restoreScreens(park.id, null);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('ws:screen', { detail: { kind: 'frame', itemId: park.id } }),
        );
      }
    } else if (park.kind === 'keep' && keptSheet) {
      // The screen stays (as a kept one); park on its sheet once it exists,
      // unless the person has moved on in the meantime.
      const parkedBefore = this.state.currentScreenId;
      void keptSheet
        .then((sheet) => {
          if (this.state.currentScreenId === parkedBefore) this.setCurrentScreen(sheet.id);
        })
        .catch(() => {});
    }
  }

  /**
   * "Move to screen" moved `movedIds` off the screens they were on: the
   * leave rules for what they left behind (rules 9 to 11). `before` is the
   * item list from just before the move. The canvas calls this; a drag never
   * does (the screen travels with the window).
   */
  settleAfterMove(before: WorkspaceItemDTO[], movedIds: string[]) {
    if (movedIds.length === 0) return;
    this.settleLeave(before, {
      local: true,
      movedIds,
      fullscreenBefore: this.fullscreenList(),
      parked: { current: this.state.currentScreenId, prev: this.state.prevScreenId },
    });
  }

  /**
   * Put a just-closed window back, exactly as it was: same id, same state
   * (position, size, version pin, project folder), so the app's content and
   * its collab room, both keyed by refId and never touched by a close, are
   * simply there again. The Undo half of the close toast
   * (`closed-window-undo.tsx`), the house pattern for a reversible act: the
   * Revent e2e run of 2026-09-26/27 closed a window from the keyboard (focus
   * had escaped the app frame onto the Close dot) with no confirm and no way
   * back. Idempotent by id, so an Undo racing a peer's own re-add, or
   * clicked twice, adds nothing. Same write path as `addItem` minus the
   * server resolve (the row was already resolved once): the local list, the
   * rebuild shield, the shared doc.
   */
  restoreItem(dto: WorkspaceItemDTO): void {
    if (!dto || typeof dto.id !== 'string') return;
    if (!this.state.workspace || dto.workspaceId !== this.state.workspace.id) return;
    if (this.state.items.some((it) => it.id === dto.id)) return;
    this.set({ items: [...this.state.items, dto] });
    this.recentLocalAdds.set(dto.id, Date.now());
    this.setYItem(dto);
    emitWorkspaceItemsChanged();
  }

  /**
   * Try Again on the offer `addItem` left when a window could not open
   * (`OpenFailedOffer`): the offer goes and the same open runs again from the
   * top (a fresh id, placed on the screen you are on now). A press is a
   * person's open, so the window takes the keyboard when it lands (rule 6).
   * A second failure makes a new offer; nothing else is reported here.
   */
  retryFailedOpen(seq: number): void {
    const failed = this.failedOpen;
    if (!failed || failed.seq !== seq) return;
    this.failedOpen = null;
    this.set({ openFailed: null });
    this.addItem({ ...failed.input, openedBy: 'user' }).catch(() => {
      /* withdrawn again; a transient failure made a new offer */
    });
  }

  /** Close the "Couldn't open" offer without trying again. */
  dismissFailedOpen(seq: number): void {
    if (this.state.openFailed?.seq !== seq) return;
    this.failedOpen = null;
    this.set({ openFailed: null });
  }

  bringToFront(itemId: string) {
    const maxZ = this.state.items.reduce(
      (acc, it) => Math.max(acc, it.state.zIndex ?? 0),
      0,
    );
    // Already the sole top item → no-op. Focus-driven raises now fire on
    // every click/iframe-focus (not just the header), so without this
    // guard zIndex would creep upward — and each raise would write a
    // patch — even when the stacking order can't change.
    const me = this.state.items.find((it) => it.id === itemId);
    if (
      me &&
      (me.state.zIndex ?? 0) === maxZ &&
      !this.state.items.some(
        (it) => it.id !== itemId && (it.state.zIndex ?? 0) === maxZ,
      )
    ) {
      return;
    }
    this.patchItemState(itemId, { zIndex: maxZ + 1 }, { debounceMs: 800, background: true });
  }

  /**
   * Push an item below every other widget on the canvas. Mirror of
   * `bringToFront` — picks `minZ - 1`. We allow negative `zIndex`
   * values because the schema is a plain number and there's no
   * compelling reason to renormalise the layer stack on every
   * gesture (renormalisation is O(items) and would invalidate the
   * stable-identity caches in `workspace-canvas.tsx`). Docked items
   * are excluded so a pinned tile that the user temporarily hid in
   * the dock doesn't drag the floor down for the visible canvas.
   */
  sendToBack(itemId: string) {
    const minZ = this.state.items.reduce(
      (acc, it) => (!isOnCanvas(it.state) ? acc : Math.min(acc, it.state.zIndex ?? 0)),
      Number.POSITIVE_INFINITY,
    );
    const nextZ = (Number.isFinite(minZ) ? minZ : 0) - 1;
    this.patchItemState(itemId, { zIndex: nextZ }, { debounceMs: 800, background: true });
  }

  /**
   * Move one layer forward — swap `zIndex` with the item that
   * currently sits directly above. Falls back to `bringToFront`
   * when the item is already on top, which matches Figma's
   * "step forward" behaviour (idempotent at the top).
   *
   * We scan only the visible canvas items: docked widgets live in a
   * separate stacking context (the dock bar) and shouldn't be
   * considered neighbours for layer-order purposes.
   */
  bringForward(itemId: string) {
    const me = this.state.items.find((it) => it.id === itemId);
    if (!me) return;
    const myZ = me.state.zIndex ?? 0;
    let nextAbove: { id: string; z: number } | null = null;
    for (const it of this.state.items) {
      if (it.id === itemId) continue;
      if (!isOnCanvas(it.state)) continue;
      const z = it.state.zIndex ?? 0;
      if (z <= myZ) continue;
      if (!nextAbove || z < nextAbove.z) nextAbove = { id: it.id, z };
    }
    if (!nextAbove) {
      this.bringToFront(itemId);
      return;
    }
    // Swap — bump myZ to nextAbove.z and nudge nextAbove down to
    // myZ. Two patches but the debounce coalesces them into one
    // round-trip per item.
    this.patchItemState(itemId, { zIndex: nextAbove.z }, { debounceMs: 800, background: true });
    this.patchItemState(nextAbove.id, { zIndex: myZ }, { debounceMs: 800, background: true });
  }

  /**
   * Mirror of `bringForward` — swap with the item directly below.
   * Falls back to `sendToBack` when already at the floor.
   */
  sendBackward(itemId: string) {
    const me = this.state.items.find((it) => it.id === itemId);
    if (!me) return;
    const myZ = me.state.zIndex ?? 0;
    let nextBelow: { id: string; z: number } | null = null;
    for (const it of this.state.items) {
      if (it.id === itemId) continue;
      if (!isOnCanvas(it.state)) continue;
      const z = it.state.zIndex ?? 0;
      if (z >= myZ) continue;
      if (!nextBelow || z > nextBelow.z) nextBelow = { id: it.id, z };
    }
    if (!nextBelow) {
      this.sendToBack(itemId);
      return;
    }
    this.patchItemState(itemId, { zIndex: nextBelow.z }, { debounceMs: 800, background: true });
    this.patchItemState(nextBelow.id, { zIndex: myZ }, { debounceMs: 800, background: true });
  }

  /** Flush any pending debounced PATCH calls (used on unmount). */
  flush() {
    for (const [, timer] of this.debounceTimers) clearTimeout(timer);
    this.debounceTimers.clear();
    if (this.workspaceDebounce) {
      clearTimeout(this.workspaceDebounce);
      this.workspaceDebounce = null;
    }
  }
}

const StoreContext = createContext<Store | null>(null);

export function WorkspaceStoreProvider({
  initialWorkspace,
  initialItems,
  initialContentMeta,
  anonReadOnly = false,
  children,
}: {
  initialWorkspace: WorkspaceDTO;
  initialItems: WorkspaceItemDTO[];
  /**
   * Server-batched content metadata keyed by `content.id`. Skip this
   * prop and the content cache simply starts empty — every node will
   * have to hit the network on mount, which is the legacy behaviour.
   */
  initialContentMeta?: Record<string, WorkspaceContentCache>;
  /**
   * When true the shell is being browsed by an anonymous visitor.
   * Mutations are still applied optimistically (so the UI feels
   * alive) but ultimately gated through `requireSignup` in
   * `WorkspaceAnonProvider`. This prop is forwarded through here so
   * future store actions can short-circuit network writes without
   * having to read the anon context themselves; right now we accept
   * it for symmetry with `WorkspaceAnonProvider` even though the
   * inline gate lives in `signup-gate.tsx`.
   */
  anonReadOnly?: boolean;
  children: React.ReactNode;
}) {
  const storeRef = useRef<Store | null>(null);
  if (!storeRef.current) {
    storeRef.current = new Store({
      workspace: initialWorkspace,
      items: initialItems,
      inFlight: 0,
      lastError: null,
      anonReadOnly,
      fullscreenItemId: null,
      currentScreenId: null,
      prevScreenId: null,
      overviewMode: false,
      handsetFullscreen: false,
      pendingFullscreen: null,
      desktopFullscreen: [],
      screenView: null,
      openFailed: null,
      pendingAnnotation: null,
      viewportAnchor: null,
      viewportRect: null,
      viewportBox: null,
      contentCache: initialContentMeta ?? {},
    });
  }

  // The store is seeded once, so a later RSC payload (a `router.refresh()`
  // after a rename, say) would otherwise never reach `state.workspace`.
  useEffect(() => {
    storeRef.current!.syncWorkspaceFromServer(initialWorkspace);
  }, [initialWorkspace]);

  useEffect(() => {
    const store = storeRef.current!;
    return () => {
      store.flush();
    };
  }, []);

  return (
    <StoreContext.Provider value={storeRef.current}>{children}</StoreContext.Provider>
  );
}

function useStore() {
  const store = useContext(StoreContext);
  if (!store) throw new Error('Workspace store not mounted');
  return store;
}

/**
 * Internal hook used by `WorkspaceShell` to inject the signup-gate
 * wrapped fetch into the store after both providers have mounted.
 * The store lives outside the SignupGateProvider in the component
 * tree (it has to — the bus / agent receiver depend on the store),
 * so we can't pass the wrapped fetch in at construction time.
 * Instead this bridge sets it on `useEffect` once the gate is
 * ready, which is fine because no mutation can fire before React
 * has finished mounting anyway.
 */
export function useStoreSetFetch(): (fn: typeof fetch) => void {
  const store = useStore();
  return useCallback((fn: typeof fetch) => store.setFetch(fn), [store]);
}

/**
 * Bridges the workspace-scoped Y.Doc (from `CanvasYjsProvider`) into the store
 * so canvas items are CRDT-backed: layout/add/remove sync conflict-free across
 * peers and persist to `content_room_docs`. Mount INSIDE `<CanvasYjsProvider>`
 * (needs `useCanvasYjs`) and within `<WorkspaceStoreProvider>` (needs the
 * store). Non-visual. Anonymous visitors never mount the Yjs provider, so the
 * store simply keeps rendering its SSR `initialItems` (read-only) for them.
 */
export function WorkspaceYjsBridge() {
  const store = useStore();
  const { workspaceRoom } = useCanvasYjs();
  const doc = workspaceRoom?.doc ?? null;
  const synced = workspaceRoom?.synced ?? false;
  useEffect(() => {
    if (!doc) return;
    store.attachYjs(doc);
    return () => store.detachYjs();
  }, [store, doc]);
  useEffect(() => {
    // Seed an empty doc from SSR items once the pod's authoritative state has
    // synced (idempotent; gated so peers can't double-seed).
    if (doc && synced) store.onYjsSynced();
  }, [store, doc, synced]);
  return null;
}

export function useWorkspaceState<T>(selector: (state: WorkspaceStoreState) => T): T {
  const store = useStore();
  const [snapshot, setSnapshot] = useState(() => selector(store.getSnapshot()));
  useEffect(() => {
    const update = () => setSnapshot(selector(store.getSnapshot()));
    const unsubscribe = store.subscribe(update);
    update();
    return unsubscribe;
    // selector is treated as stable; pass useCallback for non-trivial selectors.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);
  return snapshot;
}

export function useWorkspaceActions() {
  const store = useStore();
  return useMemo(
    () => ({
      setCamera: (x: number, y: number, z: number) => store.setWorkspaceCamera(x, y, z),
      renameWorkspace: (name: string) => store.renameWorkspace(name),
      updateWorkspaceSettings: (settings: Partial<WorkspaceSettings>) =>
        store.updateWorkspaceSettings(settings),
      addItem: (input: Parameters<Store['addItem']>[0]) => store.addItem(input),
      mergeRemoteItem: (dto: WorkspaceItemDTO) => store.mergeRemoteItem(dto),
      spawnFromAgent: (input: Parameters<Store['spawnFromAgent']>[0]) =>
        store.spawnFromAgent(input),
      spawnChild: (input: Parameters<Store['spawnChild']>[0]) => store.spawnChild(input),
      patchItemState: (
        id: string,
        patch: Patch,
        opts?: { debounceMs?: number; persist?: boolean; background?: boolean },
      ) => store.patchItemState(id, patch, opts),
      removeItem: (id: string) => store.removeItem(id),
      restoreItem: (dto: WorkspaceItemDTO) => store.restoreItem(dto),
      retryFailedOpen: (seq: number) => store.retryFailedOpen(seq),
      dismissFailedOpen: (seq: number) => store.dismissFailedOpen(seq),
      bringToFront: (id: string) => store.bringToFront(id),
      sendToBack: (id: string) => store.sendToBack(id),
      bringForward: (id: string) => store.bringForward(id),
      sendBackward: (id: string) => store.sendBackward(id),
      countByRef: (refId: string) => store.countByRef(refId),
      findFirstByRef: (refId: string) => store.findFirstByRef(refId),
      defaultStateFor: (type: WorkspaceItemType, at?: { x: number; y: number }) =>
        store.defaultStateFor(type, at),
      setFullscreenItem: (id: string | null) => store.setFullscreenItem(id),
      setCurrentScreen: (id: string | null) => store.setCurrentScreen(id),
      restoreScreens: (current: string | null, prev: string | null) =>
        store.restoreScreens(current, prev),
      setOverviewMode: (on: boolean) => store.setOverviewMode(on),
      setHandsetFullscreen: (on: boolean) => store.setHandsetFullscreen(on),
      requestFullscreenOnOpen: (id: string) => store.requestFullscreenOnOpen(id),
      clearPendingFullscreen: () => store.clearPendingFullscreen(),
      recallFullscreenOnLoad: () => store.recallFullscreenOnLoad(),
      enterDesktopFullscreen: (
        id: string,
        back: Parameters<Store['enterDesktopFullscreen']>[1],
      ) => store.enterDesktopFullscreen(id, back),
      exitDesktopFullscreen: (id: string) => store.exitDesktopFullscreen(id),
      settleAfterMove: (before: WorkspaceItemDTO[], movedIds: string[]) =>
        store.settleAfterMove(before, movedIds),
      setScreenView: (rect: ScreenRect | null) => store.setScreenView(rect),
      setPendingAnnotation: (pending: PendingAnnotation) =>
        store.setPendingAnnotation(pending),
      setViewportAnchor: (anchor: { x: number; y: number } | null) =>
        store.setViewportAnchor(anchor),
      setViewportBox: (box: { width: number; height: number } | null) =>
        store.setViewportBox(box),
      setViewportRect: (
        rect: { x: number; y: number; width: number; height: number } | null,
      ) => store.setViewportRect(rect),
      seedContent: (meta: WorkspaceContentCache) => store.seedContent(meta),
    }),
    [store],
  );
}

/**
 * Look up cached content metadata by `content.id`. Returns `undefined`
 * if the workspace was loaded before the content was added (or the
 * meta endpoint hasn't returned yet) — node renderers should fall back
 * to their own fetch in that case.
 */
export function useWorkspaceContent(refId: string | null | undefined):
  | WorkspaceContentCache
  | undefined {
  return useWorkspaceState(
    useCallback((s) => (refId ? s.contentCache[refId] : undefined), [refId]),
  );
}

export function useFullscreenItemId(): string | null {
  return useWorkspaceState(useCallback((s) => s.fullscreenItemId, []));
}

/** The window that last failed to open, with Try again, or null (`OpenFailedOffer`). */
export function useOpenFailed(): OpenFailedOffer | null {
  return useWorkspaceState(useCallback((s) => s.openFailed ?? null, []));
}

/**
 * The ids of the items this workspace loaded with, fixed for the page's life
 * (`Store.loadedItemIds`): what Stage's one-time tidy may offer.
 */
export function useLoadedItemIds(): ReadonlySet<string> {
  return useStore().loadedItemIds;
}

export function useCurrentScreenId(): string | null {
  return useWorkspaceState(useCallback((s) => s.currentScreenId, []));
}

export function usePrevScreenId(): string | null {
  return useWorkspaceState(useCallback((s) => s.prevScreenId, []));
}

/**
 * ONE AD PER DEVICE (ads build plan section 2.4): the top window by zIndex
 * among the non-minimized app windows on the current screen, or null. Only
 * that window's title-bar slot is active (node-frame.tsx). The rule is pure
 * in lib/workspaces/top-ad-window.ts; this memo runs it once per store
 * version rather than once per frame per subscriber, since every NodeFrame
 * subscribes and a drag streams a store update per animation frame.
 */
let topAdMemo: {
  items: WorkspaceItemDTO[];
  screen: string | null;
  fullscreen: DesktopFullscreenRecord[] | undefined;
  result: string | null;
} | null = null;
export function useTopAdWindowId(): string | null {
  return useWorkspaceState(
    useCallback((s) => {
      if (
        topAdMemo &&
        topAdMemo.items === s.items &&
        topAdMemo.screen === s.currentScreenId &&
        topAdMemo.fullscreen === s.desktopFullscreen
      ) {
        return topAdMemo.result;
      }
      // A window this device shows full screen lives on its own temporary
      // screen (rule 13), not on the screen it came from: while you look at
      // that screen, its top window is the one showing, so the full-screen
      // window (hidden there) must not win the title-bar slot. The rule
      // itself (lib/workspaces/top-ad-window.ts) is unchanged.
      const away = (s.desktopFullscreen ?? []).filter((r) => r.id !== s.currentScreenId);
      const pool = away.length > 0 ? s.items.filter((it) => !away.some((r) => r.id === it.id)) : s.items;
      const result = topAdWindowId(pool, s.currentScreenId);
      topAdMemo = { items: s.items, screen: s.currentScreenId, fullscreen: s.desktopFullscreen, result };
      return result;
    }, []),
  );
}

export function useOverviewMode(): boolean {
  return useWorkspaceState(useCallback((s) => s.overviewMode, []));
}

const NO_FULLSCREEN: DesktopFullscreenRecord[] = [];

/** This device's desktop full-screen records (rules 12 to 17), in the order they went full screen. */
export function useDesktopFullscreen(): DesktopFullscreenRecord[] {
  return useWorkspaceState(useCallback((s) => s.desktopFullscreen ?? NO_FULLSCREEN, []));
}

/**
 * The window whose temporary full-screen screen the view is on, or null.
 * While it is set the canvas draws that window over the whole canvas under
 * its app bar and hides everything else, the rail and every title bar stand
 * down, and its app bar holds the device's one ad slot.
 */
export function useDesktopFullscreenShownId(): string | null {
  return useWorkspaceState(
    useCallback(
      (s) =>
        s.currentScreenId && (s.desktopFullscreen ?? NO_FULLSCREEN).some((r) => r.id === s.currentScreenId)
          ? s.currentScreenId
          : null,
      [],
    ),
  );
}

/** The phone's in-tab full screen flag (per device, never in the document). */
export function useHandsetFullscreen(): boolean {
  return useWorkspaceState(useCallback((s) => s.handsetFullscreen, []));
}

/** The window an app link asked to open full screen, until the canvas has shown it. */
export function usePendingFullscreen(): { id: string; at: number } | null {
  return useWorkspaceState(useCallback((s) => s.pendingFullscreen ?? null, []));
}

/**
 * THE WINDOW A HANDSET PRESENTS (handoff 2026-09-29, section 4; the pure
 * rule is `presentedWindowId` in lib/workspaces/handset-present.ts): the
 * parked screen's own window, or a blank screen's top member window, or
 * null. Device-agnostic here; every consumer gates it on the handset test
 * (`!isMd || handsetLandscape`), the same gate the framing and the clamp
 * use, so a desktop never presents. Memoised per store version like
 * `useTopAdWindowId`, since every NodeFrame and the mobile bar subscribe.
 */
let presentedMemo: { items: WorkspaceItemDTO[]; screen: string | null; result: string | null } | null = null;
export function usePresentedWindowId(): string | null {
  return useWorkspaceState(
    useCallback((s) => {
      if (presentedMemo && presentedMemo.items === s.items && presentedMemo.screen === s.currentScreenId) {
        return presentedMemo.result;
      }
      const result = presentedWindowId(s.items, s.currentScreenId);
      presentedMemo = { items: s.items, screen: s.currentScreenId, result };
      return result;
    }, []),
  );
}

export function usePendingAnnotation(): PendingAnnotation {
  return useWorkspaceState(useCallback((s) => s.pendingAnnotation, []));
}

/**
 * Per-item selector — read a single workspace item by id with a shallow-
 * stable identity. Returns the same object reference across renders if the
 * underlying item didn't change, so consumers can use it as a React.memo
 * comparison key without spurious re-renders during drag/resize.
 *
 * The selector itself is created with `useCallback([id])` so the
 * subscription only re-attaches when `id` changes — switching items in a
 * node never tears down the listener for unrelated items.
 */
export function useWorkspaceItem(id: string | null | undefined): WorkspaceItemDTO | undefined {
  return useWorkspaceState(
    useCallback((s) => (id ? s.items.find((it) => it.id === id) : undefined), [id]),
  );
}

export function useWorkspaceItems(): WorkspaceItemDTO[] {
  return useWorkspaceState(useCallback((s) => s.items, []));
}

export function useWorkspace(): WorkspaceDTO {
  return useWorkspaceState(useCallback((s) => s.workspace, []));
}

export function useInFlight(): number {
  return useWorkspaceState(useCallback((s) => s.inFlight, []));
}

export function useLastError(): string | null {
  return useWorkspaceState(useCallback((s) => s.lastError, []));
}
